/* eslint-disable indent */
import Rendition from "../rendition";
import ExDefaultViewManager from "./managers/default/index";
import ExContinuousViewManager from "./managers/continuous/index";
import ExIframeView from "./managers/views/iframe";
import { EVENTS, DOM_EVENTS, BODY_EVENTS, MEDIA_EVENT } from "./utils/constants";
import { defer, isFloat, extend, uuid } from "../utils/core";
import Hook from "./utils/hook";
import ExLayout from "./layout";
import ExThemes from "./themes";
import ExEpubCFI from "./epubcfi";
import ExAnnotations from "./annotations";
import debounce from "lodash/debounce";

class ExRendition extends Rendition{
	constructor(book, options){
		super(book, options);

		//置換為新的 Hook
		 this.hooks = {};
		 this.hooks.display = new Hook(this);
		 this.hooks.serialize = new Hook(this);
		 this.hooks.content = new Hook(this);
		 this.hooks.unloaded = new Hook(this);
		 this.hooks.layout = new Hook(this);
		 this.hooks.render = new Hook(this);
		 this.hooks.show = new Hook(this);
 
		 this.hooks.content.register(this.handleLinks.bind(this));
		 this.hooks.content.register(this.passEvents.bind(this));
		 this.hooks.content.register(this.adjustImages.bind(this));

		//修正 minSpreadWidth 預設值為 null
		//新增 disableContextMenu 預設值為 true
		//新增 isWakeAppSelectionController 預設值為 false
		//新增 條漫 預設值為 true
		this.settings = extend(this.settings || {}, {
			defaultFontFamily: 'initial',
			defaultFontSize: 16,
            defaultLineHeight: '1.4',
			showHighlightFluorescent:false,
			showReplaceMediaImageBG:true,
			removeAudioOnContinuous: false,
			minSpreadWidth: null,
			disableContextMenu: true,
			isWebtoon: false,
			isWakeAppSelectionController: false
		});
		extend(this.settings, options);

		//加入目前初始章節屬性
		this.initSection = null;

		//使用繼承的Themes
		this.themes = new ExThemes(this);

		//置換ExEpubCFI
		this.epubcfi = new ExEpubCFI();

		//置換ExAnnotations
		this.annotations = new ExAnnotations(this);

		//建立bookmark
		if(!this.settings.isHidden && this.settings.bookmark){
			$.each(this.settings.bookmark, (index, bookmarkData)=>{
				this.addBookmark(bookmarkData);
			});
		}

		//建立highlight
		if(!this.settings.isHidden && this.settings.highlight){
			$.each(this.settings.highlight, (index, highlightData)=>{
				this.addHighlight(highlightData);
			});
		}

		//加入暫存的 display target
		this._lastAction = null; 

		//是否觸發 layout end
		this._triggerLayoutEnd = false;

		//是否觸發 remap
		this._triggerRemap = false;

		//swip判斷資料
		this._swipData = {
			start: {
				timeStamp:null,
				viewerX:null,
				viewerY:null,
				scalerX:null,
				scalerY:null,
				clientX:null,
				clientY:null

			},
			end: {
				timeStamp:null,
				viewerX:null,
				viewerY:null,
				scalerX:null,
				scalerY:null,
				clientX:null,
				clientY:null

			},
			isTriggerSelection: false,
			isBookFinish:false
		}

		//gesture判斷資料
		this._appGesture = null;
		this._gestureData = {
			isGesture:false
		}

		//click判斷資料
		this._clickData = {
			emitClick:true,
			timeStamp:null,
			breakType:null,
			breakElement: null
		}

		//重置雙指縮放計時器
		this._resetZoomCounter = null;

		//滾輪完讀事件去震動計時器
		this._bookFinishCounter = null;

		//雙擊計時器
		this._dblClickCounter = null;

		//全螢幕後的spread設定
		this._spreadAfterFullscreen = null;

		//是否於離開全螢幕時觸發Spread
		this._isTriggerSpreadAfterFullScreenEnd = false;

		//是否先觸發mark
		this._isMarkTriggerFirst = false;

		//最後事件資料
		this._lastEventData = null;

		//目前事件content
		this._lastEventContents = null;

		//取消一切捲動行為
		this.cancelAllScroll = false;

		//於內容放大後開始拖曳
		this.isStartDragFromScale = false;
		
		//取消因捲動而造成的relocated事件
		this.cancelRelocatedByScroll = false;

		//保存 container element
		this._element = null;

		//加入display debounce
		this._debounceDisplay = debounce(this.debounceDisplay.bind(this), 1000);

		//瀏覽器是否正在縮放
		this._isBroswerPinchZoom = false;

		//是否為虛擬事件
		this._isFakeEvent = false;

		//滑動手勢和自定義選取判斷時間, < 判斷為滑動手勢, > 判斷為自定義選取
		this._proSelectionCheckTime = 200;
		this._proSelectionCheckDist = 10;


	}

	//是否為手勢
	isGesture(appGesture){
		this._appGesture = null;
	}

	/**
	 * Call to attach the container to an element in the dom
	 * Container must be attached before rendering can begin
	 * @param  {element} element to attach to
	 * @return {Promise}
	 */
	 attachTo(element){

		//保存 container element
		if(!this._element){
			this._element = element;
		}

		return this.q.enqueue(function () {

			// Start rendering
			this.manager.render(element, {
				"width"  : this.settings.width,
				"height" : this.settings.height
			});

			/**
			 * Emit that rendering has attached to an element
			 * @event attached
			 * @memberof Rendition
			 */
			this.emit(EVENTS.RENDITION.ATTACHED);

		}.bind(this));

	}

	//新增書籤
	addBookmark(bookmarkData){
		let {cfi, cfieven, uuid, highlight_text, page} = bookmarkData;
		if(!this.epubcfi.isCpctString(cfi)){
			this.annotations.bookmark(cfi, {"cfieven":cfieven, "uuid":uuid, "highlight_text":highlight_text, "page":page});
		}else{
			let section = this.book.spine.get(this.epubcfi.getSpineIndexFromCpct(cfi));
			if(section){
				section.addCpctBookmark({"cfi":cfi, "cfieven":cfieven, "uuid":uuid, "highlight_text":highlight_text, "page":page});
			}
		}
	}

	//刪除書籤
	removeBookmark(bookmarkData){
		let {cfi} = bookmarkData;
		this.annotations.removeBookmark(cfi);
		let bookmark = null, bookmarkUuid = null;
		if(this.location){
			if(this.manager.name === "continuous"){
				if(this.location.center){
					bookmark = this.getCurrentBookmark(this.location.center.start.cfi, this.location.center.end.cfi);
				}
			}else{
				bookmark = this.getCurrentBookmark(this.location.start.cfi, this.location.end.cfi);
			}
		}
		if(bookmark){
			bookmarkUuid = bookmark.data.uuid;
		}
		this.emit(EVENTS.RENDITION.NOWBOOKMARK, bookmarkUuid);
	}

	//檢查書籤
	checkBookmark(bookmarkData){
		let {cfi, cfieven, uuid, highlight_text, page} = bookmarkData;
		if(!this.epubcfi.isCpctString(cfi)){
			return this.annotations.checkBookmark(cfi, {"cfieven":cfieven, "uuid":uuid, "highlight_text":highlight_text, "page":page});
		}else{
			let section = this.book.spine.get(this.epubcfi.getSpineIndexFromCpct(cfi));
			if(section){
				return section.checkCpctBookmark({"cfi":cfi, "cfieven":cfieven, "uuid":uuid, "highlight_text":highlight_text, "page":page});
			}else{
				return false;
			}
		}
	}

	//新增劃線物件
	addHighlight(highlightData){
		let {cfi, cfieven, uuid, highlight_text, page, text, jotting, color} = highlightData;
		let fluorescent = this.settings.showHighlightFluorescent ? '_fluorescent':'';

		let highlightClass = `highlight_${color}${fluorescent}`;
		if((jotting && jotting !== "") || (text && text !== "")){
			highlightClass = `highlight_${color}${fluorescent}_with_text`;
		}
		this.annotations.highlight(cfi, {"cfieven":cfieven, "uuid":uuid, "highlight_text":highlight_text, "page":page, "class":highlightClass}, null, highlightClass);
	}

	//更新劃線物件
	updateHighlight(highlightData){
		this.removeHighlight(highlightData);
		this.addHighlight(highlightData);
	}

	//刪除劃線物件
	removeHighlight(highlightData){
		let {cfi} = highlightData;
		this.annotations.removeHighlight(cfi);
	}

	//檢查書籤
	checkHighlight(highlightData){
		let {cfi, cfieven, uuid, highlight_text, page, text, jotting, color} = highlightData;
		let fluorescent = this.settings.showHighlightFluorescent ? '_fluorescent':'';

		let highlightClass = `highlight_${color}${fluorescent}`;
		if((jotting && jotting !== "") || (text && text !== "")){
			highlightClass = `highlight_${color}${fluorescent}_with_text`;
		}
		return this.annotations.checkHighlight(cfi, {"cfieven":cfieven, "uuid":uuid, "highlight_text":highlight_text, "page":page, "class":highlightClass}, null, highlightClass);
	}

	//比較CFI順序
	compareCFI(epubcfiA, epubcfiB){
		if(this.epubcfi.compare(epubcfiA, epubcfiB) < 0){
			return -1;
		}else{
			return 1;
		}
	}

	//比較頁面順序
	comparePage(cpctA, cpctB){
		if(this.epubcfi.comparePage(cpctA, cpctB) < 0){
			return -1;
		}else{
			return 1;
		}
	}

	//是否為cpct格式
	isCpctString(str){
		return this.epubcfi.isCpctString(str);
	}

	//更新是否觸發remap
	updateTriggerRemap(value){
		this.settings.triggerRemap = value;
		if(!this.settings.triggerRemap){
			this.book.locations.stopGeneratePageListProcess();
		}
	}

	//取得tts文字
	getTTSText(){
		let TTSText = "";
		if(this.location){
			let section = this.book.spine.get(this.location.start.cfi);
			let exCFI = new ExEpubCFI();
			let rangeCFI = exCFI.getRangeFromStartEndCFI(this.location.start.cfi, this.location.end.cfi);
			let cfi = new ExEpubCFI(rangeCFI);
			let range = cfi.toRange(section.document);
			TTSText = range.toString();
		}
		return TTSText;
	}

	/**
	 * Start the rendering
	 * @return {Promise} rendering has started
	 */
	start(){
		if (!this.settings.layout && (this.book.package.metadata.layout === "pre-paginated" || this.book.displayOptions.fixedLayout === "true")) {
			this.settings.layout = "pre-paginated";
		}
		//調整為完全依viewer設定
		if(!this.settings.spread){
			switch(this.book.package.metadata.spread) {
				case "none":
					this.settings.spread = "none";
					break;
				case 'landscape':
				case 'portrait':
				case 'auto':
				case "both":
					this.settings.spread = true;
					break;
			}
		}

		if(!this.manager) {

			//修正 document meta 預設值 (app only)
			this.resetMetaContent(this.settings.manager);

			this.ViewManager = this.requireManager(this.settings.manager);
			this.View = this.requireView(this.settings.view);

			this.manager = new this.ViewManager({
				view: this.View,
				queue: this.q,
				request: this.book.load.bind(this.book),
				settings: this.settings,
				section: this.initSection
			}, this);

			//將themes加入manager的render chain
			this.themes.hookManagerEvent(this.manager);
		}

		//加入搜尋事件
		this.book.locations.on(EVENTS.LOCATIONS.GET_SEARCH_MATCHES, matches => {
			this.emit(EVENTS.RENDITION.GET_SEARCH_MATCHES, matches);
		});
		this.book.locations.on(EVENTS.LOCATIONS.SEARCH_COMPLETE, () => {
			this.emit(EVENTS.RENDITION.SEARCH_COMPLETE);
		});
		this.book.locations.on(EVENTS.LOCATIONS.STOP_SEARCH, () => {
			this.emit(EVENTS.RENDITION.STOP_SEARCH);
		});
		this.book.locations.on(EVENTS.LOCATIONS.GET_CPCT_CFI, (cfi) => {
			this.emit(EVENTS.RENDITION.GET_CPCT_CFI, cfi);
		});

		//加入 generateRangeList 設定值的判讀
		if(this.settings.generateRangeList){
			this.book.locations.on(EVENTS.LOCATIONS.GEN_PAGE_START, (remapEndData) => {
				this.emit(EVENTS.RENDITION.GEN_PAGE_START, remapEndData);
			});
			this.book.locations.on(EVENTS.LOCATIONS.GEN_PAGE_PROGRESS, (progressData) => {
				this.emit(EVENTS.RENDITION.GEN_PAGE_PROGRESS, progressData);
			});
			this.book.locations.on(EVENTS.LOCATIONS.GEN_PAGE_END, (remapEndData) => {
				this.emit(EVENTS.RENDITION.GEN_PAGE_END, remapEndData);
			});
			this.book.locations.on(EVENTS.LOCATIONS.GEN_PAGE_ERROR, (remapErrorData) => {
				this.settings.triggerRemap = false;
				this.emit(EVENTS.RENDITION.GEN_PAGE_ERROR, remapErrorData);
			});

			if(this.settings.generateContentText){
				this.book.locations.on(EVENTS.LOCATIONS.GEN_TTS_START, (remapEndData) => {
					this.emit(EVENTS.RENDITION.GEN_TTS_START, remapEndData);
				});
				this.book.locations.on(EVENTS.LOCATIONS.GEN_TTS_PROGRESS, (progressData) => {
					this.emit(EVENTS.RENDITION.GEN_TTS_PROGRESS, progressData);
				});
				this.book.locations.on(EVENTS.LOCATIONS.GEN_TTS_END, (remapEndData) => {
					this.emit(EVENTS.RENDITION.GEN_TTS_END, remapEndData);
				});
				this.book.locations.on(EVENTS.LOCATIONS.GEN_TTS_ERROR, (remapErrorData) => {
					this.settings.triggerRemap = false;
					this.emit(EVENTS.RENDITION.GEN_TTS_ERROR, remapErrorData);
				});
			}


			this._triggerLayoutEnd = true;
			this._triggerRemap = true;
			this.settings.triggerRemap = true;
		}

		this.direction(this.book.package.metadata.direction || this.settings.defaultDirection);

		// Parse metadata to get layout props
		this.settings.globalLayoutProperties = this.determineLayoutProperties(this.book.package.metadata);

		this.flow(this.settings.globalLayoutProperties.flow);

		this.layout(this.settings.globalLayoutProperties);

		// Listen for displayed views
		this.manager.on(EVENTS.MANAGERS.ADDED, this.afterDisplayed.bind(this));
		this.manager.on(EVENTS.MANAGERS.REMOVED, this.afterRemoved.bind(this));

		// Listen for resizing
		this.manager.on(EVENTS.MANAGERS.RESIZED, this.onResized.bind(this));

		// Listen for layout end
		this.manager.on(EVENTS.MANAGERS.RESIZED_DISPLAY, this.onResizeDisplay.bind(this));

		// Listen for rotation
		this.manager.on(EVENTS.MANAGERS.ORIENTATION_CHANGE, this.onOrientationChange.bind(this));

		// Listen for scroll start
		this.manager.on(EVENTS.MANAGERS.START_SCROLL, this.startScroll.bind(this));

		// Listen for scroll changes
		this.manager.on(EVENTS.MANAGERS.SCROLLED, this.reportLocation.bind(this));

		// 加入內容縮放監聽
		this.manager.on(EVENTS.MANAGERS.CONTENT_SCALE, this.reportContentScale.bind(this));

		/**
		 * Emit that rendering has started
		 * @event started
		 * @memberof Rendition
		 */
		this.emit(EVENTS.RENDITION.STARTED);

		// Start processing queue
		this.starting.resolve();
	}

	//調整 minSpreadWidth 預設值為"auto"
	//-- http://www.idpf.org/epub/301/spec/epub-publications.html#meta-properties-rendering
	/**
	 * Determine the Layout properties from metadata and settings
	 * @private
	 * @param  {object} metadata
	 * @return {object} properties
	 */
	 determineLayoutProperties(metadata){
		var properties;
		var layout = this.settings.layout || metadata.layout || "reflowable";
		var spread = this.settings.spread || metadata.spread || "auto";
		var orientation = this.settings.orientation || metadata.orientation || "auto";
		var flow = this.settings.flow || metadata.flow || "auto";
		var viewport = metadata.viewport || "";
		var minSpreadWidth = this.settings.minSpreadWidth || metadata.minSpreadWidth || "auto";
		var direction = this.settings.direction || metadata.direction || "ltr";
		var webToon = (layout === "pre-paginated" && this.settings.isWebtoon);

		if ((this.settings.width === 0 || this.settings.width > 0) &&
				(this.settings.height === 0 || this.settings.height > 0)) {
			// viewport = "width="+this.settings.width+", height="+this.settings.height+"";
		}

		properties = {
			layout : layout,
			spread : spread,
			orientation : orientation,
			flow : flow,
			viewport : viewport,
			minSpreadWidth : minSpreadWidth,
			direction: direction,
			webToon: webToon
		};

		return properties;
	}

	/**
	 * Require the manager from passed string, or as a class function
	 * @param  {string|object} manager [description]
	 * @return {method}
	 */
	requireManager(manager) {
		var viewManager;

		// If manager is a string, try to load from imported managers
		if (typeof manager === "string" && manager === "default") {
			viewManager = ExDefaultViewManager;
		} else if (typeof manager === "string" && manager === "continuous") {
			viewManager = ExContinuousViewManager;
		} else {
			// otherwise, assume we were passed a class function
			viewManager = manager;
		}

		return viewManager;
	}

	/**
	 * Require the view from passed string, or as a class function
	 * @param  {string|object} view
	 * @return {view}
	 */
	requireView(view) {
		var View;

		// If view is a string, try to load from imported views,
		if (typeof view == "string" && view === "iframe") {
			View = ExIframeView;
		} else {
			// otherwise, assume we were passed a class function
			View = view;
		}

		return View;
	}

	//搜尋
	search(searchString){
		this.book.locations.search(searchString);
	}

	//停止搜尋
	stopSearch(searchString){
		this.book.locations.stopSearch();
	}

	//取得新增書籤時CFI資料
	getBookmarkCFIData(){
		if(this.location.center){
			return this.annotations.getBookmarkCFIData(this.location.center);
		}else{
			return this.annotations.getBookmarkCFIData(this.location);
		}
	}

	//移除所有選取範圍
	removeAllRanges(){
		if(this.manager && this.manager.views){
			this.manager.views.forEach((view)=>{
				let doc = view.document;
				if(doc && doc.defaultView){
					let selection = doc.defaultView.getSelection();
					if(selection && !selection.isCollapsed && selection.type === "Range"){
						selection.removeAllRanges();
						view.contents.isContentMenu = false;
						this.triggerClearRangeEvent();
					}
				}
			});
		}
	}

	//轉換為舊版CFI
	conventOldCFIToCFI(oldCFI){
		return this.epubcfi.getCFIFromOldCFI(oldCFI);
	}

	//轉換為舊版CFI
	conventCFIToOldCFI(newCFI){
		return this.epubcfi.conventCFIToOldCFI(newCFI);
	}

	//轉換為新版rangeCFI
	conventOldRangeToRange(oldRangeCFI){
		return this.epubcfi.getRangeFromOldRange(oldRangeCFI);
	}

	//轉換為舊版rangeCFI
	conventRangeToOldRange(newRangeCFI, isAddBodyBox = false){
		return this.epubcfi.conventRangeToOldRange(newRangeCFI, isAddBodyBox);
	}

	/**
	 * Adjust the layout of the rendition to reflowable or pre-paginated
	 * @param  {object} settings
	 */
	 layout(settings){
		if (settings) {
			this._layout = new ExLayout(settings, this);
			this._layout.spread(settings.spread, this.settings.minSpreadWidth);

			// this.mapping = new Mapping(this._layout.props);

			this._layout.on(EVENTS.LAYOUT.UPDATED, (props, changed) => {
				this.emit(EVENTS.RENDITION.LAYOUT, props, changed);
			});
		}

		if (this.manager && this._layout) {

			this.manager.applyLayout(this._layout);
		}

		return this._layout;
	}

	switchManager(manager){
		if(typeof manager === "string" && (manager === "default" || manager === "continuous") ){
			
			if (this.manager){
				//發出閱讀設定更改事件
				this.updateReadSetting(false);

				//清空畫面
				this.manager.clear();

				//重設 pinch zoo, 並修正 document meta 預設值 (app only)
				this.resetMetaContent(manager,false);
				
				//移除manager監聽
				this.manager.off(EVENTS.MANAGERS.ADDED, this.afterDisplayed.bind(this));
				this.manager.off(EVENTS.MANAGERS.REMOVED, this.afterRemoved.bind(this));
				this.manager.off(EVENTS.MANAGERS.RESIZED, this.onResized.bind(this));
				this.manager.off(EVENTS.MANAGERS.RESIZED_DISPLAY, this.onResizeDisplay.bind(this));
				this.manager.off(EVENTS.MANAGERS.ORIENTATION_CHANGE, this.onOrientationChange.bind(this));
				this.manager.off(EVENTS.MANAGERS.START_SCROLL, this.startScroll.bind(this));
				this.manager.off(EVENTS.MANAGERS.SCROLLED, this.reportLocation.bind(this));
				this.manager.off(EVENTS.MANAGERS.CONTENT_SCALE, this.reportContentScale.bind(this));

				//移除manager
				this.manager.destroy();
				
				//建立manager
				this.settings.manager = manager;
				this.ViewManager = this.requireManager(this.settings.manager);
				this.manager = new this.ViewManager({
					view: this.View,
					queue: this.q,
					request: this.book.load.bind(this.book),
					settings: this.settings,
					section: this.initSection
				}, this);

				//設定manager
				this.direction(this.book.package.metadata.direction || this.settings.defaultDirection);
				this.flow(this.settings.globalLayoutProperties.flow);
				this.layout();

				// Listen for displayed views
				this.manager.on(EVENTS.MANAGERS.ADDED, this.afterDisplayed.bind(this));
				this.manager.on(EVENTS.MANAGERS.REMOVED, this.afterRemoved.bind(this));

				// Listen for resizing
				this.manager.on(EVENTS.MANAGERS.RESIZED, this.onResized.bind(this));

				// Listen for layout end
				this.manager.on(EVENTS.MANAGERS.RESIZED_DISPLAY, this.onResizeDisplay.bind(this));

				// Listen for rotation
				this.manager.on(EVENTS.MANAGERS.ORIENTATION_CHANGE, this.onOrientationChange.bind(this));

				// Listen for scroll start
				this.manager.on(EVENTS.MANAGERS.START_SCROLL, this.startScroll.bind(this));

				// Listen for scroll changes
				this.manager.on(EVENTS.MANAGERS.SCROLLED, this.reportLocation.bind(this));

				// 加入內容縮放監聽
				this.manager.on(EVENTS.MANAGERS.CONTENT_SCALE, this.reportContentScale.bind(this));

				//恢復預設縮放比例並發出事件
				this.reportContentScale(1);

				//重新加入到畫面上
				this.attachTo(this._element);

				//重新display
				this.onReaderSettingDisplay();
			}
		}
	}

	/**
	 * Adjust the flow of the rendition to paginated or scrolled
	 * (scrolled-continuous vs scrolled-doc are handled by different view managers)
	 * @param  {string} flow
	 */
	 flow(flow){
		
		var _flow = flow;
		if (flow === "scrolled" || flow === "scrolled-doc" || flow === "scrolled-continuous") {
			_flow = "scrolled";
		}

		if (flow === "auto" || flow === "paginated") {
			_flow = "paginated";
		}

		this.settings.flow = flow;

		if (this._layout) {
			this._layout.flow(_flow);
		}

		if (this.manager && this._layout) {
			this.manager.applyLayout(this._layout);
		}

		if (this.manager) {
			this.manager.updateFlow(_flow);
		}

		if (this.manager && this.manager.isRendered() && this.location) {
			this.manager.clear();
			this.display(this.location.start.cfi);
		}

	}

	scale(scale, scaleXPo, scaleYPo){
		this.cancelRelocatedByScroll = true;
		this.manager.contentScaleIndex = this.manager.contentScaleRange.indexOf(scale);
		this.manager.scaleConetnt(scale, scaleXPo, scaleYPo);
	}

	/**
	 * Adjust if the rendition uses spreads
	 * @param  {string} spread none | auto (TODO: implement landscape, portrait, both)
	 * @param  {int} [min] min width to use spreads at
	 */
	 spread(spread, min){

		if(!this.manager.isDocumentFullscreen()){

			//發出閱讀設定更改事件
			this.updateReadSetting();
			
			//清空畫面
			this.manager.clear();

			this.settings.spread = spread;

			if (min) {
				this.settings.minSpreadWidth = min;
			}

			if (this._layout) {
				let axis = this.manager.settings.axis;
				if (axis === "vertical") {
					this._layout.spread(`vertical_${spread}`,min);
				} else {
					this._layout.spread(spread, min);
				}
			}

			//加入 layout end 觸發點
			this._triggerLayoutEnd = true;

			//從 前景rendition 暫停 背景 rendition
			if(this.settings.generateRangeList && this.settings.triggerRemap){
				//加入 remap 觸發點
				this._triggerRemap = true;
				//暫停背景流程
				this.book.locations.passGeneratePageListProcess();
			}

			//重新display
			this.onReaderSettingDisplay();
		}else{
			this._spreadAfterFullscreen = spread;
			this._isTriggerSpreadAfterFullScreenEnd = (this._spreadAfterFullscreen !== this.settings.spread);
		}
		
	}

	//書籍方向更改事件
	updateBookDirection(dir){
		this.book.package.metadata.direction = dir;
		this.settings.globalLayoutProperties = this.determineLayoutProperties(this.book.package.metadata);
		if (this._layout) {
			this._layout.settings.direction = dir;
		}
		if(this.manager){
			this.manager.direction(dir);
		}
		this.emit(EVENTS.RENDITION.CHANGE_BOOK_DIRECTION, this.book.package.metadata.direction);
	}

	//閱讀設定更改事件
	updateReadSetting(isTriggerRemap = true){
		this.emit(EVENTS.RENDITION.CHANGE_READ_SETTING);

		//加入 layout end 觸發點
		//this._triggerLayoutEnd = true;

		//從 前景rendition 暫停 背景 rendition
		if(isTriggerRemap && this.settings.generateRangeList && this.settings.triggerRemap){
			//加入 remap 觸發點
			this._triggerRemap = true;
			//暫停背景流程
			this.book.locations.passGeneratePageListProcess();
		}
	}

	displayFixedlayoutPage(pageNum){
		let spineIndex = this.book.spine.getSpineIndexByPage(pageNum);
		if(spineIndex !== undefined && spineIndex !== null){
			this.display(spineIndex);
		}
	}

	/**
	 * Display a point in the book
	 * The request will be added to the rendering Queue,
	 * so it will wait until book is opened, rendering started
	 * and all other rendering tasks have finished to be called.
	 * @param  {string} target Url or EpubCFI
	 * @return {Promise}
	 */
	display(target){

		//修正預設target
		if(!target || target === "" || target === "epubcfi(0/1/1)"){
			target = undefined;
		}

		//兼容 cfi range
		if(this.epubcfi.isCfiRangeString(target)){
			target = this.epubcfi.getStartFromCfiRangeString(target);
		}

		if (this.displaying) {
			this.displaying.resolve();
		}

		let section = this._setInitSection(target);
		//若取不到章節就從第一頁開始顯示
		if(!section){
			target = undefined;
		}

		//resize 前, 最後動作
		this._lastAction = {
			action: "display",
			target: target
		};

		return this.q.enqueue(this._display, target);
	}

	debounceDisplay(target){

		//修正預設target
		if(!target || target === "" || target === "epubcfi(0/1/1)"){
			target = undefined;
		}

		//兼容 cfi range
		if(this.epubcfi.isCfiRangeString(target)){
			target = this.epubcfi.getStartFromCfiRangeString(target);
		}

		if (this.displaying) {
			this.displaying.resolve();
		}

		let section = this._setInitSection(target);
		//若取不到章節就從第一頁開始顯示
		if(!section){
			target = undefined;
		}

		//resize 前, 最後動作
		this._lastAction = {
			action: "display",
			target: target
		};

		return this.q.enqueue(this._display, target);
	}

	spineDisplay(spineItemsIndex, percentage = null){
		if (this.displaying) {
			this.displaying.resolve();
		}
		return this.q.enqueue(this._spineDisplay, spineItemsIndex, percentage);
	}

	_spineDisplay(spineItemsIndex, percentage){
		
		if (!this.book) {
			return;
		}
		var displaying = new defer();
		var displayed = displaying.promise;
		var section;

		this.displaying = displaying;

		section = this.book.spine.get(spineItemsIndex);

		if(!section){
			displaying.reject(new Error("No Section Found"));
			return displayed;
		}

		this.manager.spineDisplay(section)
			.then(() => {
				displaying.resolve(section);
				this.displaying = undefined;
				this.emit(EVENTS.RENDITION.DISPLAYED, section);
				this.reportSpineAllLoction(percentage);

				//刪除目前紀錄最後動作
				this._lastAction = null; 

			}, (err) => {
				this.emit(EVENTS.RENDITION.DISPLAY_ERROR, err);
			});

		return displayed;
	}

	reportSpineAllLoction(percentage){
		return this.q.enqueue(function reportedLocation(){
			requestAnimationFrame(function reportedLocationAfterRAF() {
				let spineAllLocation = this.manager.getSpineAllLocation();
				if (spineAllLocation && spineAllLocation.then && typeof spineAllLocation.then === "function") {
					spineAllLocation.then(function(result) {
						//背景 rendition
						let allLocated = this.parseAllLocated(resul, percentage);
						if (!allLocated || !allLocated.length) {
							return;
						}
						this.emit(EVENTS.RENDITION.ALLLOCATED, allLocated);
					}.bind(this));
				} else if (spineAllLocation) {
					//背景 rendition
					let allLocated = this.parseAllLocated(spineAllLocation, percentage);
					if (!allLocated || !allLocated.length) {
						return;
					}
					this.emit(EVENTS.RENDITION.ALLLOCATED, allLocated);
				}
			}.bind(this));
		}.bind(this));
	}

	caculateLocation(locationArray = [], percentage){
		return this.q.enqueue(function caculateLocation(){
			requestAnimationFrame(function caculateLocationAfterRAF() {
				var location = this.manager.currentLocation();
				if (location && location.then && typeof location.then === "function") {
					location.then(function(result) {
						this.collectLocation(result, locationArray, percentage);
					}.bind(this));
				} else if (location) {
						this.collectLocation(location, locationArray, percentage);
				}

			}.bind(this));
		}.bind(this));
	}

	collectLocation(location, locationArray, percentage){
		let located = this.pageListLocated(location);
		if (!located || !located.start || !located.end) {
			return;
		}
		if(locationArray.length && locationArray[locationArray.length - 1].start.displayed.page === located.start.displayed.page){
			console.log(locationArray, located);
			return;
		}
		locationArray.push(located);
		this.location = located;

		
		if(percentage){
			let nowPencentage = ((this.location.end.displayed.page/this.location.end.displayed.total) * 100).toFixed(3);
			if(Array.isArray(percentage)){
				let conventItemNum = 0;
				percentage.forEach((percentageItem, index) => {
					if(!this.epubcfi.isCfiString(percentageItem)){
						if(parseFloat(nowPencentage) >= parseFloat(percentageItem)){
							percentage[index] = this.location.start.cfi;
							conventItemNum += 1;
						}
					}else{
						conventItemNum += 1;
					}
				});
				if(percentage.length - conventItemNum === 0){
					this.emit(EVENTS.RENDITION.GETCACULATELOCATION, percentage);
				}else{
					if (this.manager.isPaginated){
						if (this.manager.settings.axis === "vertical") {
							this.manager.scrollBy(0, this.manager.layout.height, true);
						}else{
							this.manager.scrollBy(this.manager.layout.delta, 0, true);
						}
					}
					this.caculateLocation(locationArray, percentage);
				}
			}else{
				if(parseFloat(nowPencentage) >= parseFloat(percentage)){
					this.emit(EVENTS.RENDITION.GETCACULATELOCATION, [this.location.start.cfi]);
				}else{
					if (this.manager.isPaginated){
						if (this.manager.settings.axis === "vertical") {
							this.manager.scrollBy(0, this.manager.layout.height, true);
						}else{
							this.manager.scrollBy(this.manager.layout.delta, 0, true);
						}
					}
					this.caculateLocation(locationArray, percentage);
				}
			}
		}else{
			if(this.location.end.displayed.page !== this.location.end.displayed.total){
				if (this.manager.isPaginated){
					if (this.manager.settings.axis === "vertical") {
						this.manager.scrollBy(0, this.manager.layout.height, true);
					}else{
						this.manager.scrollBy(this.manager.layout.delta, 0, true);
					}
				}
				this.caculateLocation(locationArray, percentage);
			}else{
				this.emit(EVENTS.RENDITION.GETCACULATELOCATION, locationArray);
			}
		}
	}


	/**
	 * Get a Range from a Visible CFI
	 * @param  {string} cfi EpubCfi String
	 * @param  {string} ignoreClass
	 * @return {range}
	 */
	 getRange(cfi, ignoreClass){
		 //置換ExEpubCFI
		var _cfi = new ExEpubCFI(cfi);
		var found = this.manager.visible().filter(function (view) {
			if(_cfi.spinePos === view.index) return true;
		});

		// Should only every return 1 item
		if (found.length) {
			return found[0].contents.range(_cfi, ignoreClass);
		}
	}

	_setInitSection(target){
		var isCfiString = this.epubcfi.isCfiString(target);
		if(isCfiString && this.book && this.book.locations.length() && isFloat(target)){
			target = this.book.locations.cfiFromPercentage(parseFloat(target));
		}
		var section = this.book.spine.get(target);

		if(section){
			this.initSection = section;
			return this.initSection;
		}else{
			return null;
		}
	}

	parseAllLocated(allLocation, percentage){
		let allLocated = [];
		let start = allLocation[0];
		let end = allLocation[allLocation.length-1];

		if(start && start.totalCfi){
			start.totalCfi.forEach((item, index) =>{
				let itemStart = {
					index: start.index,
					href: start.href,
					pages: start.totalCfi[index].page,
					totalPages: start.totalPages,
					mapping: start.totalCfi[index].mapping
				}, itemEnd = {
					index: end.index,
					href: end.href,
					pages: end.totalCfi[index].page,
					totalPages: end.totalPages,
					mapping: end.totalCfi[index].mapping
				}

				if(percentage){
					let nowPencentage = (( ( (itemEnd.pages[itemEnd.pages.length-1]) ? itemEnd.pages[itemEnd.pages.length-1] : 1 ) /itemEnd.totalPages) * 100).toFixed(3);
					if(Array.isArray(percentage)){
						let conventItemNum = 0;
						percentage.forEach((percentageItem, index) => {
							if(!this.epubcfi.isCfiString(percentageItem)){
								if(parseFloat(nowPencentage) >= parseFloat(percentageItem)){
									percentage[index] = itemStart.mapping.start;
									conventItemNum += 1;
								}
							}else{
								conventItemNum += 1;
							}
						});
						if(percentage.length - conventItemNum === 0){
							this.emit(EVENTS.RENDITION.GETCACULATELOCATION, percentage);
						}
					}else{
						if(parseFloat(nowPencentage) >= parseFloat(percentage)){
							this.emit(EVENTS.RENDITION.GETCACULATELOCATION, [itemStart.mapping.start]);
						}
					}
				}else{
					//加入label
					let startHref = this.getCurrentHref(itemStart, "start");
					let startLabel = this.book.tocDispalyHerfToLabel[startHref];
					let endHref = this.getCurrentHref(itemEnd, "end");
					let endLabel = this.book.tocDispalyHerfToLabel[endHref];

					let located = {
						start: {
							label: startLabel,
							index: itemStart.index,
							href: itemStart.href,
							cfi: itemStart.mapping.start,
							displayed: {
								page: itemStart.pages[0] || 1,
								total: itemStart.totalPages
							}
						},
						end: {
							label: endLabel,
							index: itemEnd.index,
							href: itemEnd.href,
							cfi: itemEnd.mapping.end,
							displayed: {
								page: itemEnd.pages[itemEnd.pages.length-1] || 1,
								total: itemEnd.totalPages
							},
						}
					};

					let pageStart = this.book.pageList.pageFromCfi(itemStart.mapping.start);
					let pageEnd = this.book.pageList.pageFromCfi(itemEnd.mapping.end);

					if (pageStart != -1) {
						located.start.page = pageStart;
					}
					if (pageEnd != -1) {
						located.end.page = pageEnd;
					}

					if (itemEnd.index === this.book.spine.last().index && located.end.displayed.page >= located.end.displayed.total) {
						located.atEnd = true;
					}

					if (itemStart.index === this.book.spine.first().index && located.start.displayed.page === 1) {
						located.atStart = true;
					}

					//加入ContentText
					if(this.settings.generateContentText){
						let section = this.book.spine.get(located.start.cfi);
						let rangeCFI = this.epubcfi.getRangeFromStartEndCFI(located.start.cfi, located.end.cfi);
						let cfi = new ExEpubCFI(rangeCFI);
						let range = cfi.toRange(section.document);
						located.contentText = range.toString();
					}
					
					allLocated.push(located);
				}

				
			});
		}

		return allLocated;
	}

	pageListLocated(location){
		if (!location.length) {
			return {};
		}
		let start = location[0];
		let end = location[location.length-1];

		//移除前後章節
		//pre spine
		let preSection = this.book.spine.get(start.index).prev();
		if(preSection && (!preSection._isNowDisplay && !preSection._isBgNowDisplay)){
			preSection.unload();
		}
		let nextSection = this.book.spine.get(end.index).next();
		if(nextSection && (!nextSection._isNowDisplay && !nextSection._isBgNowDisplay)){
			nextSection.unload();
		}

		//加入label
		let startHref = this.getCurrentHref(start, "start");
		let startLabel = this.book.tocDispalyHerfToLabel[startHref];
		let endHref = this.getCurrentHref(end, "end");
		let endLabel = this.book.tocDispalyHerfToLabel[endHref];

		let located = {
			start: {
				label: startLabel,
				index: start.index,
				href: start.href,
				cfi: start.mapping.start,
				displayed: {
					page: start.pages[0] || 1,
					total: start.totalPages
				}
			},
			end: {
				label: endLabel,
				index: end.index,
				href: end.href,
				cfi: end.mapping.end,
				displayed: {
					page: end.pages[end.pages.length-1] || 1,
					total: end.totalPages
				},
			}
		};

		let pageStart = this.book.pageList.pageFromCfi(start.mapping.start);
		let pageEnd = this.book.pageList.pageFromCfi(end.mapping.end);

		if (pageStart != -1) {
			located.start.page = pageStart;
		}
		if (pageEnd != -1) {
			located.end.page = pageEnd;
		}

		if (end.index === this.book.spine.last().index &&
				located.end.displayed.page >= located.end.displayed.total) {
			located.atEnd = true;
		}

		if (start.index === this.book.spine.first().index &&
				located.start.displayed.page === 1) {
			located.atStart = true;
		}

		//加入ContentText
		if(this.settings.generateContentText){
			let section = this.book.spine.get(located.start.cfi);
			let rangeCFI = this.epubcfi.getRangeFromStartEndCFI(located.start.cfi, located.end.cfi);
			let cfi = new ExEpubCFI(rangeCFI);
			let range = cfi.toRange(section.document);
			located.contentText = range.toString();
		}

		return located;
	}

	/**
	 * Creates a Rendition#locationRange from location
	 * passed by the Manager
	 * @returns {displayedLocation}
	 * @private
	 */
	 located(location){

		if (!location.length) {
			return {};
		}
		let start = location[0];
		let end = location[location.length-1];

		//移除前後章節
		let preSection = this.book.spine.get(start.index).prev();
		if(preSection && (!preSection._isNowDisplay && !preSection._isBgNowDisplay)){
			preSection.unload();
		}
		let nextSection = this.book.spine.get(end.index).next();
		if(nextSection && (!nextSection._isNowDisplay && !nextSection._isBgNowDisplay)){
			nextSection.unload();
		}

		//加入label
		let startHref = this.getCurrentHref(start, "start");
		let startLabel = this.book.tocDispalyHerfToLabel[startHref];
		let endHref = this.getCurrentHref(end, "end");
		let endLabel = this.book.tocDispalyHerfToLabel[endHref];

		//加入bookmark和中線章節資訊
		let bookmark, centerStart, centerEnd, centerStartHref, centerEndHref, centerStartLabel, centerEndLabel;
		if(this.manager.name === "continuous"){
			let centerLocation = this.manager.centerLocation();
			centerStart = centerLocation[0];
			centerEnd = centerLocation[centerLocation.length-1];
			//加入label
			centerStartHref = this.getCurrentHref(centerStart, "start");
			centerStartLabel = this.book.tocDispalyHerfToLabel[centerStartHref];
			centerEndHref = this.getCurrentHref(centerEnd, "end");
			centerEndLabel = this.book.tocDispalyHerfToLabel[centerEndHref];

			bookmark = this.getCurrentBookmark(centerStart.mapping.start, centerEnd.mapping.end);
		}else{
			bookmark = this.getCurrentBookmark(start.mapping.start, end.mapping.end);
		}

		//加入scrollPo
		let scrollPo = null;
		switch(this.book.settings.scrollBarType){
		case "spine": let currentSpine = (this.manager.name === "continuous") ? this.book.spine.get(centerEndHref) : this.book.spine.get(end.href);
					  scrollPo = this.book.scrollBarNodeHrefToScrollPo[currentSpine.tocDisplayHref];
					  break;
		case "toc":
		case "mix": scrollPo = (this.manager.name === "continuous") ? centerEndHref : this.book.scrollBarNodeHrefToScrollPo[endHref];
			break;
		}

		let located = {
			start: {
				label: startLabel,
				index: start.index,
				href: start.href,
				cfi: start.mapping.start,
				cfiRange: start.mapping.end,
				displayed: {
					page: start.pages[0] || 1,
					total: start.totalPages
				}
			},
			center:(this.manager.name === "continuous") ? {
				start: {
					label: centerStartLabel,
					index: centerStart.index,
					href: centerStart.href,
					cfi: centerStart.mapping.start,
					cfiRange: centerStart.mapping.end,
					displayed: {
						page: centerStart.pages[0] || 1,
						total: centerStart.totalPages
					}
				},
				end: {
					label: centerEndLabel,
					index: centerEnd.index,
					href: centerEnd.href,
					cfiRange: centerEnd.mapping.start,
					cfi: centerEnd.mapping.end,
					displayed: {
						page: centerEnd.pages[centerEnd.pages.length-1] || 1,
						total: centerEnd.totalPages
					},
				}
			} : null,
			end: {
				label: endLabel,
				index: end.index,
				href: end.href,
				cfiRange: end.mapping.start,
				cfi: end.mapping.end,
				displayed: {
					page: end.pages[end.pages.length-1] || 1,
					total: end.totalPages
				},
			},
			scrollbar: {
				position: scrollPo,
				total: this.book.scrollBarNodeList.length
			},
			bookmark: (bookmark) ? bookmark.data.uuid : null
		};

		let locationStart = this.book.locations.locationFromCfi(start.mapping.start);
		let locationEnd = this.book.locations.locationFromCfi(end.mapping.end);

		//加入cpct
		located.start.cpct = `epubcfi(${located.start.index}/${located.start.displayed.page}/${located.start.displayed.total})`;
		located.end.cpct = `epubcfi(${located.end.index}/${located.end.displayed.page}/${located.end.displayed.total})`;

		//加入確定到小數第二位
		if (locationStart != null) {
			located.start.location = locationStart;
			located.start.percentage = this.book.locations.percentageFromLocation(locationStart).toFixed(2);
		}
		if (locationEnd != null) {
			located.end.location = locationEnd;
			located.end.percentage = this.book.locations.percentageFromLocation(locationEnd).toFixed(2);
		}

		let pageStart = this.book.pageList.pageFromCfi(start.mapping.start);
		let pageEnd = this.book.pageList.pageFromCfi(end.mapping.end);

		if (pageStart != -1) {
			located.start.page = pageStart;
		}
		if (pageEnd != -1) {
			located.end.page = pageEnd;
		}

		if (end.index === this.book.spine.last().index && located.end.displayed.page >= located.end.displayed.total) {
				if(this.settings.manager === "continuous"){
					let container = this.manager.container;
					let isHorizontal = (this.book.package.metadata.layout === "pre-paginated") ? false : this.manager.settings.axis === "horizontal" ;
					if(isHorizontal && Math.ceil(container.scrollLeft + container.offsetWidth) >= container.scrollWidth){
						located.atEnd = true;
					} else if( Math.ceil(container.scrollTop + container.offsetHeight) >= container.scrollHeight ){
						located.atEnd = true;
					}
				}else{
					located.atEnd = true;
				}
		}

		if (start.index === this.book.spine.first().index &&
				located.start.displayed.page === 1) {
			located.atStart = true;
		}

		//加入ContentText
		if(this.settings.generateContentText){
			let section = this.book.spine.get(located.start.cfi);
			let exCFI = new ExEpubCFI();
			let rangeCFI = exCFI.getRangeFromStartEndCFI(located.start.cfi, located.end.cfi);
			let cfi = new ExEpubCFI(rangeCFI);
			let range = cfi.toRange(section.document);
			located.contentText = range.toString();
		}

		return located;
	}

	getCurrentBookmark(startCfi, endCfi){
		let start_cfi = new ExEpubCFI(startCfi);
		let sectionStartIndex = start_cfi.spinePos;
		let end_cfi = new ExEpubCFI(endCfi);
		let sectionEndIndex = end_cfi.spinePos;
		let bookmarkHashArray;
		if(sectionStartIndex === sectionEndIndex){
			bookmarkHashArray = this.annotations._bookmarkAt(sectionEndIndex);
		}else{
			let startBookmarkHashArray = this.annotations._bookmarkAt(sectionStartIndex) || [];
			let endBookmarkHashArray = this.annotations._bookmarkAt(sectionEndIndex) || [];
			bookmarkHashArray = startBookmarkHashArray.concat(endBookmarkHashArray);
			if(!bookmarkHashArray.length){
				bookmarkHashArray = undefined;
			}
		}
		let containAnnotationArray = [];
		let bookmark = null;
		if(bookmarkHashArray){
			$.each(bookmarkHashArray, (index, hash)=>{
				let annotation = this.annotations._annotations[hash];
				let bookmarkCFI = annotation.cfiRange;
				if( (startCfi === endCfi && this.epubcfi.compare(bookmarkCFI, startCfi) == 0) || this.book.package.metadata.layout === "pre-paginated"){
					containAnnotationArray.push(annotation);
				}else if(this.epubcfi.compare(bookmarkCFI, startCfi) >= 0 && this.epubcfi.compare(bookmarkCFI, endCfi) < 0){
					containAnnotationArray.push(annotation);
				}
			});
			if(containAnnotationArray.length){
				$.each(containAnnotationArray, (index, annotation)=>{
					if(bookmark){
						if(this.epubcfi.compare(bookmark.cfiRange, annotation.cfiRange) < 0){
							bookmark = annotation;
						}
					}else{
						bookmark = annotation;
					}
				});
			}
		}
		return bookmark;
	}

	getCurrentHref(section, mappingKey){
		let currentHref = null;
		let currentSection = this.book.spine.get(section.href);
		let sectionHashs = currentSection.getSectionHash();
		let hash = null;
		let lastHashIndex;
		if(sectionHashs && sectionHashs.length){
			$.each(sectionHashs, function(hashIndex, hashItem){
				lastHashIndex = hashIndex;
				if(hashItem.cfi !== ""){
					let compareResult = this.epubcfi.compare(hashItem.cfi, section.mapping[mappingKey]);
					if(compareResult == 0){
						hash = hashItem.hash;
						return false;
					}else if(compareResult > 0){
						hash = (hashIndex == 0) ? sectionHashs[hashIndex].hash : sectionHashs[hashIndex-1].hash;
						return false;
					}
				}	
			}.bind(this));
			if(hash === null){
				hash = sectionHashs[lastHashIndex].hash;
			}
			if(hash !== ""){
				currentHref = `${section.href}#${hash}`;
				if(!this.book.tocDispalyHerfToLabel[currentHref]){
					currentHref = `${currentSection.idref}#${hash}`;
					if(!this.book.tocDispalyHerfToLabel[currentHref]){
						currentHref = `${currentSection.filename}#${hash}`;
					}
				}
			}else{
				currentHref = currentSection.tocDisplayHref;
			}
		}else{
			currentHref = currentSection.tocDisplayHref;
		}
		return currentHref;
	}

	/**
	 * Tells the manager what to display immediately
	 * @private
	 * @param  {string} target Url or EpubCFI
	 * @return {Promise}
	 */
	 _display(target){
		
		if (!this.book) {
			return;
		}
		var displaying = new defer();
		var displayed = displaying.promise;
		var section;

		this.displaying = displaying;

		// Check if this is a book percentage
		if (this.book.locations.length() && isFloat(target)) {
			target = this.book.locations.cfiFromPercentage(parseFloat(target));
		}
		section = this.book.spine.get(target);

		if(!section){
			displaying.reject(new Error("No Section Found"));
			return displayed;
		}

		this.manager.display(section, target)
			.then(() => {
				displaying.resolve(section);
				this.displaying = undefined;

				/**
				 * Emit that a section has been displayed
				 * @event displayed
				 * @param {Section} section
				 * @memberof Rendition
				 */
				this.emit(EVENTS.RENDITION.DISPLAYED, section);
				this.reportLocation();

				//刪除目前紀錄最後動作
				this._lastAction = null; 

			}, (err) => {
				/**
				 * Emit that has been an error displaying
				 * @event displayError
				 * @param {Section} section
				 * @memberof Rendition
				 */
				this.emit(EVENTS.RENDITION.DISPLAY_ERROR, err);
			});

		return displayed;
	}

	/**
	 * Report what section has been displayed
	 * @private
	 * @param  {*} view
	 */
	 afterDisplayed(view){

		//調整為兩段式觸發, 已區隔 mark 和 iframe 的事件
		view.on(EVENTS.VIEWS.MARK_MOUSE_DOWN, (e, cfiRange, data) => this.triggerMarkMouseDownEvent(e, cfiRange, data, view.contents));
		view.on(EVENTS.VIEWS.MARK_MOUSE_UP, (e, data) => this.triggerMarkMouseUpEvent(e, data, view.contents));
		view.on(EVENTS.VIEWS.WAKE_SELECTION_CONTROLLER, (point) => this.triggerWakeSelectionControllerEvent(point));

		this.hooks.render.trigger(view, this)
			.then(() => {

				//調整回傳參數
				let renderedData = {
					spineInfo: view.section,
					section_is_vertical: (view.settings.axis === "vertical"),
					section_is_fixlayout: (view.layout.name === "pre-paginated")
				};

				if (view.contents) {
					this.hooks.content.trigger(view.contents, this).then(() => {

						//為最後一個載入的 section 才發 rendition 事件
						if(view.section.idref === this.manager.finalEventTarget.idref){	

							/**
							 * Emit that a section has been rendered
							 * @event rendered
							 * @param {Section} section
							 * @param {View} view
							 * @memberof Rendition
							 */
							this.emit(EVENTS.RENDITION.RENDERED, renderedData);

							//判斷是否發出 layout end
							if(this._triggerLayoutEnd){
								this.emit(EVENTS.RENDITION.LAYOUT_END, this._layout.props);
								this._triggerLayoutEnd = false;
							}

						}			
					});

				} else {

					//為最後一個載入的 section 才發 rendition 事件
					if(view.section.idref === this.manager.finalEventTarget.idref){

						this.emit(EVENTS.RENDITION.RENDERED, renderedData);

						//判斷是否發出 layout end
						if(this._triggerLayoutEnd){
							this.emit(EVENTS.RENDITION.LAYOUT_END, this._layout.props);
							this._triggerLayoutEnd = false;
						}
						
					}
				}
			});

	}

	/**
	 * Hook to adjust images to fit in columns
	 * @param  {Contents} contents
	 * @private
	 */
	 adjustImages(contents) {

		if (this._layout.name === "pre-paginated") {
			return new Promise(function(resolve){
				resolve();
			});
		}

		//fix columnWidth
		let section = this.manager.nowSection;
		let stageSize = this.manager.stage.size();

		if(!this.manager.isPaginated) {
			this._layout.calculate(section, stageSize.width, stageSize.height);
		} else {
			this._layout.calculate(
				section,
				stageSize.width,
				stageSize.height,
				this.settings.gap
			);
		}
		let columnWidth = this._layout.columnWidth;

		//get horizontalPadding
		let computed = contents.window.getComputedStyle(contents.content, null);
		let height = (contents.content.offsetHeight - (parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom))) * .95;
		let horizontalPadding = parseFloat(computed.paddingLeft) + parseFloat(computed.paddingRight);

		contents.addStylesheetRules({
			"img" : {
				"max-width": (columnWidth ? (columnWidth - horizontalPadding) + "px" : "100%"),
				"max-height": height + "px",
				"object-fit": "contain",
				"page-break-inside": "avoid",
				"break-inside": "avoid",
				"box-sizing": "border-box"
			},
			"svg" : {
				"max-width": (columnWidth ? (columnWidth - horizontalPadding) + "px" : "100%"),
				"max-height": height + "px",
				"page-break-inside": "avoid",
				"break-inside": "avoid"
			}
		}, "img");

		return new Promise((resolve, reject) => {
			// Wait to apply
			setTimeout(() => {
				resolve();
			}, 1);
		});
	}

	startScroll(scrollPo){
		this.emit(EVENTS.RENDITION.START_SCROLL, scrollPo);
	}

	reportContentScale(contentScale){
		this.emit(EVENTS.RENDITION.CONTENT_SCALE, contentScale);
	}

	/**
	 * Report the current location
	 * @fires relocated
	 * @fires locationChanged
	 */
	 reportLocation(){
		return this.q.enqueue(function reportedLocation(){
			requestAnimationFrame(function reportedLocationAfterRAF() {

					//內容有放大時,依目前紀錄的捲動位置進行校正
					if(this.settings.manager !== "continuous" && this.manager._contentScale > 1){
						let container = this.manager.container;
						container.scrollLeft = this.manager._contentScrollLeft;
						container.scrollTop = this.manager._contentScrollTop;
					}

					var location = this.manager.currentLocation();
					if (location && location.then && typeof location.then === "function") {
						location.then(function(result) {
							if(!this.settings.isHidden){
								//前景 rendition
								let located = this.located(result);

								if (!located || !located.start || !located.end) {
									return;
								}

								this.emit(EVENTS.RENDITION.NOWBOOKMARK, located.bookmark);
								delete located.bookmark;

								this.location = located;

								this.emit(EVENTS.RENDITION.LOCATION_CHANGED, {
									index: this.location.start.index,
									href: this.location.start.href,
									start: this.location.start.cfi,
									startRange: this.location.start.cfiRange,
									end: this.location.end.cfi,
									endRange: this.location.end.cfiRange,
									percentage: this.location.start.percentage
								});

								let readingSetting =  {
									spread: (this._layout._spread) ? "auto" : "none",
									theme: this.themes._booksBackground,
									font: this.themes._booksFont,
									fontSize: this.themes._booksFontSize,
									lineHeight: this.themes._booksLineHeight,
									width:this._layout.props.width,
									height:this._layout.props.height
								};

								this.emit(EVENTS.RENDITION.RELOCATED, this.location);

								//判斷是否發出 remap
								if(this._triggerRemap && this.settings.generateRangeList && this.settings.triggerRemap){
									//從 前景rendition 啟動 背景 rendition
									this.book.locations.startGeneratePageListProcess();
									this._triggerRemap = false;
								}

							}else{
								//背景 rendition
								let located = this.pageListLocated(result);
								if (!located || !located.start || !located.end) {
									return;
								}
								this.location = located;
								this.emit(EVENTS.RENDITION.RELOCATED, this.location);
							}
						}.bind(this));
					} else if (location) {
						if(!this.settings.isHidden){
							//前景 rendition
							let located = this.located(location);

							if (!located || !located.start || !located.end) {
								return;
							}

							this.emit(EVENTS.RENDITION.NOWBOOKMARK, located.bookmark);
							delete located.bookmark;

							this.location = located;

							/**
							 * @event locationChanged
							 * @deprecated
							 * @type {object}
							 * @property {number} index
							 * @property {string} href
							 * @property {EpubCFI} start
							 * @property {EpubCFI} end
							 * @property {number} percentage
							 * @memberof Rendition
							 */
							this.emit(EVENTS.RENDITION.LOCATION_CHANGED, {
								index: this.location.start.index,
								href: this.location.start.href,
								start: this.location.start.cfi,
								startRange: this.location.start.cfiRange,
								end: this.location.end.cfi,
								endRange: this.location.end.cfiRange,
								percentage: this.location.start.percentage
							});

							let readingSetting =  {
								spread: (this._layout._spread) ? "auto" : "none",
								theme: this.themes._booksBackground,
								font: this.themes._booksFont,
								fontSize: this.themes._booksFontSize,
								lineHeight: this.themes._booksLineHeight,
								width:this._layout.props.width,
								height:this._layout.props.height
							};

							/**
							 * @event relocated
							 * @type {displayedLocation}
							 * @memberof Rendition
							 */
							this.emit(EVENTS.RENDITION.RELOCATED, this.location);

							//判斷是否發出 remap
							if(this._triggerRemap && this.settings.generateRangeList && this.settings.triggerRemap){
								//從 前景rendition 啟動 背景 rendition
								this.book.locations.startGeneratePageListProcess();
								this._triggerRemap = false;
							}

						}else{
							//背景 rendition
							let located = this.pageListLocated(location);
							if (!located || !located.start || !located.end) {
								return;
							}
							this.location = located;
							this.emit(EVENTS.RENDITION.RELOCATED, this.location);
						}
					}

			}.bind(this));
		}.bind(this));
	}

	loadSection(section){
		this.emit(EVENTS.RENDITION.LOAD_SECTION, section);
	}

	resetMetaContent(manager, inInit = true){
		if(!this.book.settings.encryptType || (this.book.settings.encryptType && this.settings.isDomTriggerByTouch)){
			let viewportmeta = document.querySelector('meta[name=viewport]');
			if(inInit){
				switch(manager){
					case "default": viewportmeta.setAttribute('content', "width=device-width, initial-scale=1.0");
									break;
					case "continuous": //viewportmeta.setAttribute('content', "width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0");
									   viewportmeta.setAttribute('content', "width=device-width, initial-scale=1.0");
									break;
				}
				this.removeAllRanges();
			}else{
				viewportmeta.setAttribute('content', "width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0");
				this.manager.views.addSegmentation();
				clearTimeout(this._resetZoomCounter);
				this._resetZoomCounter = setTimeout( () => {
					switch(manager){
						case "default": viewportmeta.setAttribute('content', "width=device-width, initial-scale=1.0");
										break;
						case "continuous": //viewportmeta.setAttribute('content', "width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0");
											viewportmeta.setAttribute('content', "width=device-width, initial-scale=1.0");
										break;
					}
					this.manager.views.clearSegmentation();
					this.removeAllRanges();
				}, 100);
			}
		}
	}

	/**
	 * Go to the next "page" in the rendition
	 * @return {Promise}
	 */
	 next(){
		//app reset pinch zoom
		if(window.visualViewport.scale.toFixed(2) > 1){
			this.resetMetaContent(this.settings.manager,false);
		}
		return this.q.enqueue(this.manager.next.bind(this.manager))
			.then(this.reportLocation.bind(this), (err) => {
				/**
				 * Emit that has been an error displaying
				 * @event displayError
				 * @param {Section} section
				 * @memberof Rendition
				 */
				this.emit(EVENTS.RENDITION.DISPLAY_ERROR, err);
			});
	}

	/**
	 * Go to the previous "page" in the rendition
	 * @return {Promise}
	 */
	prev(){
		//app reset pinch zoom
		if(window.visualViewport.scale.toFixed(2) > 1){
			this.resetMetaContent(this.settings.manager,false);
		}
		return this.q.enqueue(this.manager.prev.bind(this.manager))
			.then(this.reportLocation.bind(this), (err) => {
				/**
				 * Emit that has been an error displaying
				 * @event displayError
				 * @param {Section} section
				 * @memberof Rendition
				 */
				this.emit(EVENTS.RENDITION.DISPLAY_ERROR, err);
			});
	}

	triggerMediaEvent(e, contents){
		let mediaUrl = e.target.currentSrc;
		let isPlaying = ($(e.target).attr('data-is-playing') === "yes");
		switch(e.type){
			case "play": 
			case "pause":
			case "seeking":
			case "volumechange": 
			case "ratechange": 	 
			case "enterpictureinpicture":
			case "leavepictureinpicture": 
			case "webkitfullscreenchange":
			case "mozfullscreenchange":
			case "fullscreenchange": this.emit(EVENTS.RENDITION.CONTROL_MEDIA, e, mediaUrl, isPlaying);
									 break;
		}
	}

	triggerFakeVideoImageEvent(e, contents){
		let videoUrl = $(e.target).attr('data-media-src');
		this.emit(EVENTS.RENDITION.OPEN_VIDEO_PLAYER, e, videoUrl);
	}

	triggerFakeAudioImageEvent(e, contents){
		let audioUrl = $(e.target).attr('data-media-src');
		this.emit(EVENTS.RENDITION.OPEN_AUDIO_PLAYER, e, audioUrl);
	}

	triggerMarkMouseDownEvent(e, cfiRange, data, contents){
		this._isMarkTriggerFirst = true;
	}

	triggerMarkMouseUpEvent(e, data, contents){
		this._isMarkTriggerFirst = false;
		/**
		 * Emit that a mark was clicked
		 * @event markClicked
		 * @param {EpubCFI} cfirange
		 * @param {object} data
		 * @param {Contents} contents
		 * @memberof Rendition
		 */
		this.emit(EVENTS.RENDITION.MARK_CLICKED, e, data, contents);
	}

	conventToGlobalPosition(position){
		let scale = this.manager.getContentScale();
		let containerScrollLeft = this.manager.container.scrollLeft;
        let containerScrollTop = this.manager.container.scrollTop;
		let globalPosition;

		if(this._layout.name !== "pre-paginated"){
			//流動版
			let dir = this.manager.settings.direction;
			let containerOffsetLeft = this.manager.container.offsetLeft;
			let containerOffsetTop = this.manager.container.offsetTop;
            let containerScrollWidth = this.manager.container.scrollWidth;
            let containerOffsetWidth = this.manager.container.offsetWidth;
			let containerScrollDist = containerScrollWidth - containerOffsetWidth;
			if(!dir || dir === "ltr"){
				//ltr
				if(Array.isArray(position)){
					globalPosition = [];
					Object.entries(position).forEach( entry => {
							let [key] = entry;
							globalPosition[key] = {};
							globalPosition[key].x = containerOffsetLeft + ( position[key].x - containerScrollLeft );
							globalPosition[key].y = containerOffsetTop + ( position[key].y - containerScrollTop );
						});
                }else{
					globalPosition = {};
                    globalPosition.x = containerOffsetLeft + ( position.x - containerScrollLeft );
                    globalPosition.y = containerOffsetTop + ( position.y - containerScrollTop );
                }
			}else{
				//rtl
				if(Array.isArray(position)){
					globalPosition = [];
					if(this.manager.settings.rtlScrollType === "default"){
						containerScrollLeft = containerScrollLeft - containerScrollDist;
					}
					Object.entries(position).forEach( entry => {
							let [key] = entry;
							globalPosition[key] = {};
							globalPosition[key].x = (containerOffsetLeft - containerScrollDist) + ( position[key].x - containerScrollLeft );
							globalPosition[key].y = containerOffsetTop + ( position[key].y - containerScrollTop );	
						});
                }else{
					globalPosition = {};
                    globalPosition.x = (containerOffsetLeft - containerScrollDist) + ( position.x - containerScrollLeft );
                    globalPosition.y = containerOffsetTop + ( position.y - containerScrollTop );
                }
			}
		}else{
			//固定版
			let view = $(e.view.frameElement).parent().get(0);
			let viewOffectLeft = view.offsetLeft;
			let viewOffectTop = view.offsetTop; 
			let parentViewOffset = $(e.view.frameElement).parent().offset();
			if(Array.isArray(position)){
				globalPosition = [];
				Object.entries(position).forEach(entry => {
					let [key] = entry;
					globalPosition[key] = {};
					globalPosition[key].x = parentViewOffset.left + (position[key].x * scale);
					globalPosition[key].y = parentViewOffset.top + (position[key].y * scale);
					globalPosition[key].scalerX = viewOffectLeft + position[key].x;
					globalPosition[key].scalerY = viewOffectTop + position[key].y;
				});
			}else{
				globalPosition = {};
				globalPosition.x = parentViewOffset.left  + (position.x * scale);
				globalPosition.y = parentViewOffset.top + (position.y * scale);
				globalPosition.scalerX = viewOffectLeft + position.x;
				globalPosition.scalerY = viewOffectTop + position.y;
			}
		}

		return globalPosition;
	}

	/**
	 * Emit events passed by a view
	 * @private
	 * @param  {event} e
	 */
	 triggerViewEvent(e, contents){

		
		if(this._isFakeEvent){
			return true;
		}
		
		//保存最後 contents
		this._lastEventContents = contents;

		//修正clientX, clientY
		let scale = this.manager.getContentScale();
		let containerScrollLeft = this.manager.container.scrollLeft;
        let containerScrollTop = this.manager.container.scrollTop;

		if(this._layout.name !== "pre-paginated"){
			//流動版
			let dir = this.manager.settings.direction;
			let containerOffsetLeft = this.manager.container.offsetLeft;
			let containerOffsetTop = this.manager.container.offsetTop;
            let containerScrollWidth = this.manager.container.scrollWidth;
            let containerOffsetWidth = this.manager.container.offsetWidth;
			let containerScrollDist = containerScrollWidth - containerOffsetWidth;
            if(!dir || dir === "ltr"){
                if(e.changedTouches && e.changedTouches.length){
					Object.entries(e.changedTouches).forEach( entry => {
						let [key, value] = entry;
						e.changedTouches[key].viewerX = containerOffsetLeft + ( e.changedTouches[key].clientX - containerScrollLeft );
                    	e.changedTouches[key].viewerY = containerOffsetTop + ( e.changedTouches[key].clientY - containerScrollTop );
					});
					if(e.touches && e.touches.length){
						Object.entries(e.touches).forEach( entry => {
							let [key, value] = entry;
							e.touches[key].viewerX = containerOffsetLeft + ( e.touches[key].clientX - containerScrollLeft );
							e.touches[key].viewerY = containerOffsetTop + ( e.touches[key].clientY - containerScrollTop );
						});
					}
                }else{
                    e.viewerX = containerOffsetLeft + ( e.clientX - containerScrollLeft );
                    e.viewerY = containerOffsetTop + ( e.clientY - containerScrollTop );
                }
			}else{
				if(e.changedTouches && e.changedTouches.length){
					if(this.manager.settings.rtlScrollType === "default"){
						containerScrollLeft = containerScrollLeft - containerScrollDist;
					}
					Object.entries(e.changedTouches).forEach( entry => {
						let [key, value] = entry;
						e.changedTouches[key].viewerX = (containerOffsetLeft - containerScrollDist) + ( e.changedTouches[key].clientX - containerScrollLeft );
                    	e.changedTouches[key].viewerY = containerOffsetTop + ( e.changedTouches[key].clientY - containerScrollTop );	
					});
					if(e.touches && e.touches.length){
						Object.entries(e.touches).forEach( entry => {
							let [key, value] = entry;
							e.touches[key].viewerX = (containerOffsetLeft - containerScrollDist) + ( e.touches[key].clientX - containerScrollLeft );
							e.touches[key].viewerY = containerOffsetTop + ( e.touches[key].clientY - containerScrollTop );	
						});
					}
                }else{
                    e.viewerX = (containerOffsetLeft - containerScrollDist) + ( e.clientX - containerScrollLeft );
                    e.viewerY = containerOffsetTop + ( e.clientY - containerScrollTop );
                }
			}
		}else{
			//固定版
			let view = $(e.view.frameElement).parent().get(0);
			let viewOffectLeft = view.offsetLeft;
			let viewOffectTop = view.offsetTop; 
			let parentViewOffset = $(e.view.frameElement).parent().offset();
			if(e.changedTouches && e.changedTouches.length){
				Object.entries(e.changedTouches).forEach(entry => {
					let [key, value] = entry;
					e.changedTouches[key].viewerX = parentViewOffset.left + (e.changedTouches[key].clientX * scale);
					e.changedTouches[key].viewerY = parentViewOffset.top + (e.changedTouches[key].clientY * scale);
					e.changedTouches[key].scalerX = viewOffectLeft + e.changedTouches[key].clientX;
					e.changedTouches[key].scalerY = viewOffectTop + e.changedTouches[key].clientY;
				});
				if(e.touches && e.touches.length){
					Object.entries(e.touches).forEach(entry => {
						let [key, value] = entry;
						e.touches[key].viewerX = parentViewOffset.left + (e.touches[key].clientX * scale);
						e.touches[key].viewerY = parentViewOffset.top + (e.touches[key].clientY * scale); 
						e.touches[key].scalerX = viewOffectLeft + e.touches[key].clientX;
						e.touches[key].scalerY = viewOffectTop + e.touches[key].clientY;
					});
				}
			}else{
				e.viewerX = parentViewOffset.left  + (e.clientX * scale);
				e.viewerY = parentViewOffset.top + (e.clientY * scale);
				e.scalerX = viewOffectLeft + e.clientX;
				e.scalerY = viewOffectTop + e.clientY;
			}
		}

		//保存最後事件
		this._lastEventData = e;

		//mweb不支援文字選取和highlight點擊
		let isMWeb = false;
		if(this.book.settings.encryptType && this.settings.isDomTriggerByTouch){
			isMWeb = true;
		}

		//如過是mark先觸發就以mark為主
		if(this._isMarkTriggerFirst && !isMWeb){
			if(e.type === "mousemove" || e.type === "touchmove"){
				//產生移動時則取消mark優先事件
				this._isMarkTriggerFirst = false;
			}else if(e.type === "dblclick"){
				//在mark發生雙擊時的處理方式
				let selection = contents.window.getSelection();
				if (selection && !selection.isCollapsed && selection.type === "Range"){
					contents.triggerFinalSelectedEvent(e, selection);
				}else{
					contents.removePreSelectRange();
					return;
				}
			}else{
				return;
			}
		}

		//讓 流動版 的 click事件 和 selected事件 互斥
		switch(e.type){
			case "wheel":
			case "mousewheel":
			case "DOMMouseScroll": if(this._layout.name === "pre-paginated" && !isMWeb){
										if(this.settings.manager === "continuous"){
											
											//this.manager.stage.pauseScrolling();
											//this.manager.stage.recoverScrolling();

											if(e.ctrlKey){
												e.preventDefault();
												this.cancelRelocatedByScroll = true;
												e = window.event || e;
												let delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
												let scale, scaleIndex = this.manager.contentScaleIndex + delta;
												if(scaleIndex < 0){
													scaleIndex = 0;
												}else if(scaleIndex > this.manager.contentScaleRange.length - 1){
													scaleIndex = this.manager.contentScaleRange.length - 1;
												}
												this.manager.contentScaleIndex = scaleIndex;
												scale = this.manager.contentScaleRange[scaleIndex];
												this.manager.scaleConetnt(scale, e.scalerX, e.scalerY);
											}else{
												let isHorizontal = (this.book.package.metadata.layout === "pre-paginated") ? false : this.manager.settings.axis === "horizontal" ;
												if(isHorizontal){

												}else{
													let delta;
													if(this.location.atEnd && e.wheelDelta < 0){

														delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
														this.manager.stage.showAlert("v-end");

														clearTimeout(this._bookFinishCounter);
														this._bookFinishCounter = setTimeout(function(){
															e = window.event || e;
															this.emit(EVENTS.CONTENTS.BOOK_FINISH, e, contents);
														}.bind(this),100);

													}else if(this.location.atStart && e.wheelDelta > 0){
														delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
														this.manager.stage.showAlert("v-start");
													}else{
														delete this.location.atEnd;
														delete this.location.atStart;
													}
												}
											}
										}else{
											this.cancelRelocatedByScroll = true;
											e = window.event || e;
											let delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
											let scale, scaleIndex = this.manager.contentScaleIndex + delta;
											if(scaleIndex < 0){
												scaleIndex = 0;
											}else if(scaleIndex > this.manager.contentScaleRange.length - 1){
												scaleIndex = this.manager.contentScaleRange.length - 1;
											}
											this.manager.contentScaleIndex = scaleIndex;
											scale = this.manager.contentScaleRange[scaleIndex];
											this.manager.scaleConetnt(scale, e.scalerX, e.scalerY);
										}
								}
								break;
			case "dblclick":    if(this.settings.isDomTriggerByTouch){
									this.cancelAllScroll = false;
									this.checkEventPath(e, contents);
									if(!this._clickData.emitClick && this._clickData.breakType === "dblClickImg" && this.settings.layout !== "pre-paginated"){
										this.triggerPopImageEvent(e, this._clickData.breakElement);
										this.resetClickData();
									}
								}
								break;

			case "touchstart": if(this.settings.isDomTriggerByTouch){
									this._swipData.isBookFinish = false;
									if(this._appGesture !== null){
										this._gestureData.isGesture = this._appGesture;
									}else{
										if(e.touches.length > 1){
											this._gestureData.isGesture = true;
											if(window.visualViewport.scale.toFixed(2) > 1 && !this._isBroswerPinchZoom){
												this._isBroswerPinchZoom = true;
												this.emit(EVENTS.RENDITION.START_PINCH_ZOOM);
											}
										}
									}
									this._swipData.start = {
										timeStamp: e.timeStamp,
										viewerX: e.touches[0].viewerX,
										viewerY: e.touches[0].viewerY,
										scalerX: (e.touches[0].scalerX) ? e.touches[0].scalerX : null,
										scalerY: (e.touches[0].scalerY) ? e.touches[0].scalerY : null,
										clientX: e.touches[0].clientX,
										clientY: e.touches[0].clientY
									}
									if(scale > 1){
										this.isStartDragFromScale = true;
									}else{
										this.cancelAllScroll = true;
									}
									this.checkEventPath(e, contents);
									this.emit(e.type, e, contents);
								}
							   break;

			case "touchmove": if(this.settings.isDomTriggerByTouch){
									if(this.isStartDragFromScale){
										this.cancelRelocatedByScroll = true;
										let distX = e.touches[0].scalerX - this._swipData.start.scalerX;
										let distY = e.touches[0].scalerY - this._swipData.start.scalerY;
										this.manager.dragScaleContent(distX, distY);
									}
									if(window.visualViewport.scale.toFixed(2) > 1 && !this._isBroswerPinchZoom){
										this._isBroswerPinchZoom = true;
										this.emit(EVENTS.RENDITION.START_PINCH_ZOOM);
									}
									if(this.settings.manager === "continuous" && window.visualViewport.scale.toFixed(2) <= 1){
										let isHorizontal = (this.book.package.metadata.layout === "pre-paginated") ? false : this.manager.settings.axis === "horizontal" ;
										let distance = (isHorizontal) ? e.touches[0].viewerX - this._swipData.start.viewerX : e.touches[0].viewerY - this._swipData.start.viewerY;
										if(isHorizontal){
											if(distance > 0 && this.book.package.metadata.direction === "rtl"){
												delete this.location.atEnd;
												delete this.location.atStart;
											}else if(distance < 0 && this.book.package.metadata.direction === "ltr"){
												delete this.location.atEnd;
												delete this.location.atStart;
											}
										}else{
											if(!this.displaying){
												if(this.location.atEnd && distance < 0){
													this.manager.stage.showAlert("v-end");
													this._swipData.isBookFinish = true;
												}else if(this.location.atStart && distance > 0){
													this.manager.stage.showAlert("v-start");
												}else{
													delete this.location.atEnd;
													delete this.location.atStart;
												}
											}
										}
									}else if(this.settings.manager === "continuous" && window.visualViewport.scale.toFixed(2) > 1 && this._dblClickCounter){
										clearTimeout(this._dblClickCounter);
										this.resetClickData();
									}else if(!this._gestureData.isGesture && window.visualViewport.scale.toFixed(2) <= 1 && this._layout.name !== "pre-paginated" && this.settings.isWakeAppSelectionController){
										let moveData = {};
										moveData.end = {
											timeStamp: e.timeStamp,
											viewerX: e.changedTouches[0].viewerX,
											viewerY: e.changedTouches[0].viewerY,
											scalerX: (e.changedTouches[0].scalerX) ? e.changedTouches[0].scalerX : null,
											scalerY: (e.changedTouches[0].scalerY) ? e.changedTouches[0].scalerY : null,
											clientX: e.changedTouches[0].clientX,
											clientY: e.changedTouches[0].clientY
										};
										let time = moveData.end.timeStamp - this._swipData.start.timeStamp;
										let dist = Math.abs(moveData.end.viewerX - this._swipData.start.viewerX);
										if(!(time < this._proSelectionCheckTime && dist > this._proSelectionCheckDist)){
											this.manager.currentSelection(this._swipData.start, moveData.end);
											this._swipData.isTriggerSelection = true;
										}
									}
								}
								break;

			case "touchend":if(this.settings.isDomTriggerByTouch){
								this.cancelAllScroll = false;
								this._swipData.end = {
									timeStamp: e.timeStamp,
									viewerX: e.changedTouches[0].viewerX,
									viewerY: e.changedTouches[0].viewerY,
									scalerX: (e.changedTouches[0].scalerX) ? e.changedTouches[0].scalerX : null,
									scalerY: (e.changedTouches[0].scalerY) ? e.changedTouches[0].scalerY : null,
									clientX: e.changedTouches[0].clientX,
									clientY: e.changedTouches[0].clientY
								}
								this.checkRange(this._swipData, contents);
								let extraEvent = this.getExtraEvent(this._swipData, this._gestureData, this._clickData, contents, e.touches.length);
								if(scale > 1){
									this.isStartDragFromScale = false;
									if( this._swipData.end.viewerX !== this._swipData.start.viewerX || this._swipData.end.viewerY !== this._swipData.start.viewerY ){
										return;
									}
								}
								if(e.touches.length <= 1 && this._isBroswerPinchZoom){
									this._isBroswerPinchZoom = false;
									this.emit(EVENTS.RENDITION.STOP_PINCH_ZOOM);
								}
								if(this.settings.layout == "pre-paginated" && window.visualViewport.scale.toFixed(2) > 1){
									if( (this._swipData.end.viewerX !== this._swipData.start.viewerX && Math.abs(this._swipData.end.viewerX - this._swipData.start.viewerX) > 2) || (this._swipData.end.viewerY !== this._swipData.start.viewerY && Math.abs(this._swipData.end.viewerY-this._swipData.start.viewerY) > 2)){
										return;
									}
								}
								if(extraEvent){
									this.resetClickData();
									this.emit(extraEvent, e, contents);
								}else if(this._clickData.emitClick){
									if(this._layout.name !== "pre-paginated" && !isMWeb){
										setTimeout(()=>{
											let selection = contents.window.getSelection();
												if (selection && !selection.isCollapsed && selection.type === "Range"){
													contents.triggerFinalSelectedEvent(e, selection);
													this.resetClickData();
												}else{
													contents.removePreSelectRange();
													if(this._clickData.breakType !== "clearRange"){
														this.emit(e.type, e, contents);
														this.resetClickData();
													}else{
														this.resetClickData();
													}
												}
											}, 100); //ios常按會產生框詞的現象,而產生第一次選取,需延長判讀時間以確認最後一次得選取範圍
									}else{
										this.emit(e.type, e, contents);
									}
								}else{
									switch(this._clickData.breakType){
										
										case "clickFakeVideoImage": this.triggerFakeVideoImageEvent(e, contents);
																	this.resetClickData();
																	break;
										case "clickFakeAudioImage": this.triggerFakeAudioImageEvent(e, contents);
																	this.resetClickData();
																	break;
										case "clickMedia": this.resetClickData();
															break;
										case "scaleTap": clearTimeout(this._dblClickCounter);
														 this._dblClickCounter = setTimeout(function(){
															clearTimeout(this._dblClickCounter);
															if(this._layout.name !== "pre-paginated" && !isMWeb){
																setTimeout(()=>{
																	let selection = contents.window.getSelection();
																		if (selection && !selection.isCollapsed && selection.type === "Range"){
																			contents.triggerFinalSelectedEvent(e, selection);
																			this.resetClickData();
																		}else{
																			contents.removePreSelectRange();
																			this.resetClickData();
																			e.changedTouches[0].viewerX = Math.round((e.changedTouches[0].viewerX * window.visualViewport.scale - window.visualViewport.offsetLeft * window.visualViewport.scale) * 10) / 10;
																			e.changedTouches[0].viewerY = Math.round((e.changedTouches[0].viewerY * window.visualViewport.scale - window.visualViewport.offsetTop * window.visualViewport.scale) * 10) / 10;
																			this.emit(e.type, e, contents);
																		}
																	}, 1);
															}else{
																this.resetClickData();
																e.changedTouches[0].viewerX = Math.round((e.changedTouches[0].viewerX * window.visualViewport.scale - window.visualViewport.offsetLeft * window.visualViewport.scale) * 10) / 10;
																e.changedTouches[0].viewerY = Math.round((e.changedTouches[0].viewerY * window.visualViewport.scale - window.visualViewport.offsetTop * window.visualViewport.scale) * 10) / 10;
																this.emit(e.type, e, contents);
															}
														 }.bind(this),300);
														 break;
										case "scaleDblTap": this.resetMetaContent(this.settings.manager,false);
															this.resetClickData();
															break;
										case "clickImg":if(this._layout.name !== "pre-paginated"){
															clearTimeout(this._dblClickCounter);
															this._dblClickCounter = setTimeout(function(){
																clearTimeout(this._dblClickCounter);
																this._clickData = {
																	emitClick:true,
																	timeStamp:null,
																	breakType:null,
																	breakElement: null
																}
																if(this._layout.name !== "pre-paginated" && !isMWeb){
																	setTimeout(()=>{
																		let selection = contents.window.getSelection();
																			if (selection && !selection.isCollapsed && selection.type === "Range"){
																				contents.triggerFinalSelectedEvent(e, selection);
																				this.resetClickData();
																			}else{
																				contents.removePreSelectRange();
																				this.emit(e.type, e, contents);
																			}
																		}, 1);
																}else{
																	this.emit(e.type, e, contents);
																}			
															}.bind(this),300);
														}else{
															this.resetClickData();
															this.emit(e.type, e, contents);
														}
														break;
										case "dblClickImg": if(this._layout.name !== "pre-paginated"){
																this.triggerPopImageEvent(e, this._clickData.breakElement);
															}else{
																this.emit(e.type, e, contents);
															}
															this.resetClickData();
															break;
										case "outterAllow": this.triggerPopWindowEvent(e, this._clickData.breakElement);
															this.resetClickData();
															break;
										case "innerAllow":
										default:this.resetClickData();
									}
								}
							}break;

			case "mousedown":if(!this.settings.isDomTriggerByTouch){
								if(scale > 1){
									if(this.settings.manager === "default" || (this.settings.manager === "continuous" && e.ctrlKey)){
										this._swipData.start = {
											timeStamp: e.timeStamp,
											viewerX: e.viewerX,
											viewerY: e.viewerY,
											scalerX: (e.scalerX) ? e.scalerX : null,
											scalerY: (e.scalerY) ? e.scalerY : null
										}
										this.isStartDragFromScale = true;
									}else{
										this.cancelAllScroll = true;
									}
								}else{
									this.cancelAllScroll = true;
								}
								this.checkEventPath(e, contents);
								this.emit(e.type, e, contents);
							}
							break;
							
			case "mousemove":if(!this.settings.isDomTriggerByTouch){
								if(this.isStartDragFromScale){
									this.cancelRelocatedByScroll = true;
									let distX = e.scalerX - this._swipData.start.scalerX;
									let distY = e.scalerY - this._swipData.start.scalerY;
									this.manager.dragScaleContent(distX, distY);
								}
							}
							break;

			case "mouseup": if(!this.settings.isDomTriggerByTouch){
								if(scale > 1){
									if(this.settings.manager === "default" || (this.settings.manager === "continuous" && e.ctrlKey)){
										this._swipData.end = {
											timeStamp: e.timeStamp,
											viewerX: e.viewerX,
											viewerY: e.viewerY,
											scalerX: (e.scalerX) ? e.scalerX : null,
											scalerY: (e.scalerY) ? e.scalerY : null
										}
										this.isStartDragFromScale = false;
										if(this._swipData.end.viewerX !== this._swipData.start.viewerX || this._swipData.end.viewerY !== this._swipData.start.viewerY){
											return;
										}
									}
								}
								this.cancelAllScroll = false;
								if(this._clickData.emitClick){
									if(this._layout.name !== "pre-paginated" && !isMWeb){
										setTimeout(()=>{
											let selection = contents.window.getSelection();
												if (selection && !selection.isCollapsed && selection.type === "Range"){
													contents.triggerFinalSelectedEvent(e, selection);
													this.resetClickData();
												}else{
													contents.removePreSelectRange();
													if(this._clickData.breakType !== "clearRange"){
														this.emit(e.type, e, contents);
													}else{
														this.resetClickData();
													}
												}
											}, 1);
									}else{
										this.emit(e.type, e, contents);
									}
								}else{
									switch(this._clickData.breakType){
										case "clickFakeVideoImage": this.triggerFakeVideoImageEvent(e, contents);
																	this.resetClickData();
																	break;
										case "clickFakeAudioImage": this.triggerFakeAudioImageEvent(e, contents);
																	this.resetClickData();
																	break;
										case "clickMedia": this.resetClickData();
														break;
										case "clickImg": if(this._layout.name !== "pre-paginated"){
															clearTimeout(this._dblClickCounter);
															this._dblClickCounter = setTimeout(function(){
																clearTimeout(this._dblClickCounter);
																this.resetClickData();
																if(this._layout.name !== "pre-paginated" && !isMWeb){
																	setTimeout(()=>{
																		let selection = contents.window.getSelection();
																			if (selection && !selection.isCollapsed && selection.type === "Range"){
																				contents.triggerFinalSelectedEvent(e, selection);
																				this.resetClickData();
																			}else{
																				contents.removePreSelectRange();
																				this.emit(e.type, e, contents);
																			}
																		}, 1);
																}else{
																	this.emit(e.type, e, contents);
																}			
															}.bind(this),300);
														}else{
															this.resetClickData();
															this.emit(e.type, e, contents);
														}
														break;
										case "dblClickImg": if(this._layout.name !== "pre-paginated"){
																this.triggerPopImageEvent(e, this._clickData.breakElement);
															}else{
																this.emit(e.type, e, contents);
															}
															this.resetClickData();
															break;
										case "outterAllow": this.triggerPopWindowEvent(e, this._clickData.breakElement);
															this.resetClickData();
															break;
										case "innerAllow":
										default:this.resetClickData();
									}
								}
							}
							break;

			case "contextmenu": if(this._layout.name !== "pre-paginated" && !isMWeb){
									setTimeout(()=>{
										let selection = contents.window.getSelection();
											if (selection && !selection.isCollapsed && selection.type === "Range"){
												contents.triggerFinalSelectedEvent(null, selection);
												this.resetClickData();
											}else{
												contents.removePreSelectRange();
												this.resetClickData();
											}
									}, 1);
								}
								break;

		    default: this.emit(e.type, e, contents);
		}
	}

	triggerPopImageEvent(e, element){
		let imgUrl = e.target.currentSrc;
		if(!imgUrl){
			imgUrl = ($(element).attr("src")) ? $(element).attr("src") : $(element).attr("xlink:href");
		}
		this.emit(EVENTS.RENDITION.POP_IMAGE, e, imgUrl);
	}

	triggerPopWindowEvent(e, element){
		let windowUrl = $(element).attr('data-href');
		this.emit(EVENTS.RENDITION.POP_WINDOW, e, windowUrl);
	}

	triggerClearRangeEvent(e){
		this.emit(EVENTS.RENDITION.CLEAR_RANGE, e);
	}

	checkRange(swipData, contents){
		let selection = contents.window.getSelection();
		if (selection && !selection.isCollapsed && selection.type === "Range"){
			if(this.settings.isWakeAppSelectionController){
				if(!swipData.isTriggerSelection){
					selection.removeAllRanges();
					contents.isContentMenu = false;
					this.triggerClearRangeEvent();
					this._clickData.timeStamp = swipData.end.timeStamp;
					this._clickData.breakType = "clearRange";
					this._clickData.breakElement = null;
				}
			}else if(swipData.start.timeStamp !== null && swipData.start.viewerX !== null && swipData.end.timeStamp !== null && swipData.end.viewerX !== null){
				let distance = swipData.end.viewerX - swipData.start.viewerX;
				if(!distance){
					selection.removeAllRanges();
					contents.isContentMenu = false;
					this.triggerClearRangeEvent();
					this._clickData.timeStamp = swipData.end.timeStamp;
					this._clickData.breakType = "clearRange";
					this._clickData.breakElement = null;
				}
			}
		}
	}

	checkEventPath(e, contents){

		let nowTouchPoints = (e.touches && e.touches.length) ? e.touches.length : null;

		if(e.type !== 'touchstart'){
			let selection = contents.window.getSelection();
			if (selection && !selection.isCollapsed && selection.type === "Range"){
				selection.removeAllRanges();
				contents.isContentMenu = false;
				this.triggerClearRangeEvent();
				this._clickData.timeStamp = e.timeStamp;
				this._clickData.breakType = "clearRange";
				this._clickData.breakElement = null;
			}
		}

  		const path = e.path || (e.composedPath && e.composedPath());

		$.each(path, (index, element)=>{
			
			let tagName = $(element).prop("tagName");
			tagName = (tagName)? tagName.toUpperCase() : tagName;
			switch(tagName){
				case "DIV": switch($(element).attr("class")){
								case "books-video-fake-image":this._clickData.emitClick = false;
															  this._clickData.timeStamp = e.timeStamp;
															  this._clickData.breakType = "clickFakeVideoImage";
															  this._clickData.breakElement = element;
															  return false;
															  break;
								case "books-audio-fake-image":this._clickData.emitClick = false;
															  this._clickData.timeStamp = e.timeStamp;
															  this._clickData.breakType = "clickFakeAudioImage";
															  this._clickData.breakElement = element;
															  return false;
															  break;
							}
							break;
				case "A":  	switch($(element).attr("data-href-type")){
								case "outterAllow": this._clickData.emitClick = false;
													this._clickData.timeStamp = e.timeStamp;
													this._clickData.breakType = "outterAllow";
													this._clickData.breakElement = element;
													return false;
													break;
								case "innerAllow": this._clickData.emitClick = false;
												   this._clickData.timeStamp = e.timeStamp;
												   this._clickData.breakType = "innerAllow";
												   this._clickData.breakElement = element;
												   return false;
												   break;
								/*case "forbidden": this.resetClickData();
												  break;*/
							}
						   break;
				case "IMAGE":
				case "IMG": if(this._layout.name !== "pre-paginated"){
								if(!this._clickData.breakType){
									this._clickData.emitClick = false;
									this._clickData.timeStamp = e.timeStamp;
									this._clickData.breakType = "clickImg";
									this._clickData.breakElement = element;
								}else if(this._clickData.breakType === "clickImg" && ($(this._clickData.breakElement).attr("src") === $(element).attr("src") || $(this._clickData.breakElement).attr("xlink:href") === $(element).attr("xlink:href"))){
									clearTimeout(this._dblClickCounter);
									this._clickData.emitClick = false;
									this._clickData.timeStamp = e.timeStamp;
									this._clickData.breakType = "dblClickImg";
									this._clickData.breakElement = element;
								}
							}else{
								if(window.visualViewport.scale.toFixed(2) > 1){
									if(!this._clickData.breakType){
										this._clickData.emitClick = false;
										this._clickData.timeStamp = e.timeStamp;
										this._clickData.breakType = "scaleTap";
										this._clickData.breakElement = null;
									}else if(this._clickData.breakType === "scaleTap" && this._dblClickCounter && nowTouchPoints === 1){
										clearTimeout(this._dblClickCounter);
										this._clickData.emitClick = false;
										this._clickData.timeStamp = e.timeStamp;
										this._clickData.breakType = "scaleDblTap";
										this._clickData.breakElement = null;
									}
									return false;
								}
							}
							break;
				case "VIDEO":
				case "AUDIO": this._clickData.emitClick = false;
							  this._clickData.timeStamp = e.timeStamp;
							  this._clickData.breakType = "clickMedia";
							  this._clickData.breakElement = element;
							  return false;
							  break;
				case "BODY": if(window.visualViewport.scale.toFixed(2) > 1){
									if(!this._clickData.breakType){
										this._clickData.emitClick = false;
										this._clickData.timeStamp = e.timeStamp;
										this._clickData.breakType = "scaleTap";
										this._clickData.breakElement = null;
									}else if(this._clickData.breakType === "scaleTap" && this._dblClickCounter && nowTouchPoints === 1){
										clearTimeout(this._dblClickCounter);
										this._clickData.emitClick = false;
										this._clickData.timeStamp = e.timeStamp;
										this._clickData.breakType = "scaleDblTap";
										this._clickData.breakElement = null;
									}
									return false;
								}
							  break;
				default: if(window.visualViewport.scale.toFixed(2) > 1){
							if(!this._clickData.breakType){
								this._clickData.emitClick = false;
								this._clickData.timeStamp = e.timeStamp;
								this._clickData.breakType = "scaleTap";
								this._clickData.breakElement = null;
							}else if(this._clickData.breakType === "scaleTap" && this._dblClickCounter && nowTouchPoints === 1){
								clearTimeout(this._dblClickCounter);
								this._clickData.emitClick = false;
								this._clickData.timeStamp = e.timeStamp;
								this._clickData.breakType = "scaleDblTap";
								this._clickData.breakElement = null;
							}
							return false;
						}
						 break;
			}
		});
	}

	resetClickData(){
		this._clickData = {
			emitClick:true,
			timeStamp:null,
			breakType:null,
			breakElement: null
		};
	}

	getExtraEvent(swipData, gestureData, clickData, contents, nowTouchPoints){
		let extraEventType = null;
		let selection = contents.window.getSelection();
		if (!this._swipData.isTriggerSelection && selection && !selection.isCollapsed && selection.type === "Range"){
			return extraEventType;
		}
		if(this.settings.manager === "continuous"){
			if(swipData.start.viewerX !== null && swipData.start.viewerY !== null && swipData.end.viewerX !== null && swipData.end.viewerY !== null){
				let distanceX = swipData.end.viewerX - swipData.start.viewerX;
				let distanceY = swipData.end.viewerY - swipData.start.viewerY;
				if(swipData.isBookFinish){
					extraEventType = EVENTS.CONTENTS.BOOK_FINISH;
				}else{
					if(!clickData.breakType && (Math.abs(distanceX) >= 2 || Math.abs(distanceY) >= 2)){
						extraEventType = EVENTS.CONTENTS.SCROLL;
					}
				}
			}
		}else{
			if(gestureData.isGesture){
				if(this._appGesture !== null){
					gestureData.isGesture = this._appGesture;
				}else{
					if(nowTouchPoints == 0){
						gestureData.isGesture = false;
					}
				}
				extraEventType = EVENTS.CONTENTS.ZOOM;
			}else{
				if(swipData.start.timeStamp !== null && swipData.start.viewerX !== null && swipData.end.timeStamp !== null && swipData.end.viewerX !== null){
					let distance = swipData.end.viewerX - swipData.start.viewerX;
					let time = swipData.end.timeStamp - swipData.start.timeStamp;
					let rate = distance/time;
					if(window.visualViewport.scale.toFixed(2) <= 1){
						if(!this._swipData.isTriggerSelection){
							if(Math.abs(rate) >= 0.3){
								extraEventType = (distance > 0) ? EVENTS.CONTENTS.SWIP_RIGHT : EVENTS.CONTENTS.SWIP_LEFT ;
							}
						}else if(this.settings.isWakeAppSelectionController){
							selection = this.manager.currentSelection(swipData.start, swipData.end);
							if (selection && !selection.isCollapsed && selection.type === "Range"){
								contents.triggerFinalSelectedEvent(null, selection, {viewerX : swipData.start.viewerX, viewerY : swipData.start.viewerY});
								extraEventType = 'proSelection';
							}
							this._swipData.isTriggerSelection = false;
						}
					}else{
						if(Math.abs(distance) > 1){
							extraEventType = EVENTS.CONTENTS.ZOOM_TAP;
						}
					}
				}
			}
		}
		return extraEventType;
	}

	/**
	 * Pass the events from a view's Contents
	 * @private
	 * @param  {Contents} view contents
	 */
	 passEvents(contents){
		DOM_EVENTS.forEach((e) => {
			contents.on(e, (ev) => this.triggerViewEvent(ev, contents));
		});
		BODY_EVENTS.forEach((e) => {
			contents.on(e, (ev) => this.triggerViewEvent(ev, contents));
		});
		MEDIA_EVENT.forEach((e) => {
			contents.on(e, (ev) => this.triggerMediaEvent(ev, contents));
		});

		contents.on(EVENTS.CONTENTS.SWIP_LEFT, (e) => this.triggerSwipLeftEvent(e, contents));
		contents.on(EVENTS.CONTENTS.SWIP_RIGHT, (e) => this.triggerSwipRightEvent(e, contents));
		contents.on(EVENTS.CONTENTS.BOOK_FINISH, (e) => this.triggerBookFinishEvent(e, contents));

		//mweb不支援文字選取
		if(!this.book.settings.encryptType || (this.book.settings.encryptType && !this.settings.isDomTriggerByTouch)){
			contents.on(EVENTS.CONTENTS.SELECTED, (e) => this.triggerSelectedEvent(e, contents));
			contents.on(EVENTS.CONTENTS.BOOKSSELECTED, (mouseEvent, markData) => this.triggerFinalSelectedEvent(mouseEvent, markData, contents));
			contents.on(EVENTS.CONTENTS.WAKE_SELECTION_CONTROLLER, (point) => this.triggerWakeSelectionControllerEvent(point));
		}
	}

	triggerSwipLeftEvent(e, contents){
		this.emit(EVENTS.RENDITION.SWIP_LEFT, e, contents);
	}

	triggerSwipRightEvent(e, contents){
		this.emit(EVENTS.RENDITION.SWIP_RIGHT, e, contents);
	}

	triggerBookFinishEvent(e, contents){
		this.emit(EVENTS.RENDITION.BOOK_FINISH, e, contents);
	}

	//觸發原生選取範圍控制器,屏蔽所有事件
	triggerWakeSelectionControllerEvent(point){
		this._isFakeEvent = true;
		this.emit(EVENTS.RENDITION.WAKE_SELECTION_CONTROLLER, point);
	}

	//恢復所有事件
	recoverEvent(){
		if(this._isFakeEvent){
			this._isFakeEvent = false;
		}
	}

	/**
	 * Emit a selection event's CFI Range passed from a a view
	 * @private
	 * @param  {string} cfirange
	 */
	 triggerFinalSelectedEvent(mouseEvent, cfirange, contents){
		this.cancelAllScroll = true;
		let annotation = null;
		this.manager.views.forEach((view) => {
			if(view.highlights[cfirange.cfi] && !annotation){
				annotation = this.annotations.getAnnotationByCfiRangeAndType("highlight", cfirange.cfi);
				view.emitHighlightSelect(annotation.data);
			}
		});
		/**
		 * Emit that a text selection has occured
		 * @event selected
		 * @param {string} cfirange
		 * @param {Contents} contents
		 * @memberof Rendition
		 */
		if(!annotation){
			this.emit(EVENTS.RENDITION.BOOKSSELECTED, mouseEvent, cfirange, contents);
		}
	}

	/**
	 * Report resize events and display the last seen location
	 * @private
	 */
	 onResized(size, epubcfi){
		/**
		 * Emit that the rendition has been resized
		 * @event resized
		 * @param {number} width
		 * @param {height} height
		 * @param {string} epubcfi (optional)
		 * @memberof Rendition
		 */

		this.emit(EVENTS.RENDITION.RESIZED, {
			width: size.width,
			height: size.height
		}, epubcfi);

		//加入 layout end 觸發點
		this._triggerLayoutEnd = true;

		//從 前景rendition 暫停 背景 rendition
		if(this.settings.generateRangeList){
			//加入 remap 觸發點
			this._triggerRemap = true;
			//暫停背景流程
			this.book.locations.passGeneratePageListProcess();
		}

		//移除直接連續呼叫 display
	}

	//目前暫時沒有呼叫
	onReaderSettingDisplay(){
		//顯示 rendition 才執行
		if(!this.settings.isHidden){
			if (this.location) {
				if(this.location.center){
					this._debounceDisplay(this.location.center.start.cfi);
				}else if(this.location.start){
					//this.display(this.location.start.cfi);
					this._debounceDisplay(this.location.start.cfi);
				}
			}else if(this._lastAction){
				//處理在未取得this.location時, 以暫存呼叫 display 的 target 方式,
				//讓 display 得以再次被呼叫, 讓 No Document Available 的錯誤發生時不至於中斷顯示流程
				this.reDesplay(this._lastAction);
			}
		}
	}

	
	onResizeDisplay(epubcfi){
		//app reset pinch zoom
		if(window.visualViewport.scale.toFixed(2) > 1){
			this.resetMetaContent(this.settings.manager,false);
		}
		//顯示 rendition 才執行
		if(!this.settings.isHidden){
			if (this.location) {
				if(this.location.center){
					this._debounceDisplay(this.location.center.start.cfi);
				}else if(this.location.start){
					this._debounceDisplay(this.location.start.cfi);
					//this.display(this.location.start.cfi);
				}
			}else if(this._lastAction){
				//處理在未取得this.location時, 以暫存呼叫 display 的 target 方式,
				//讓 display 得以再次被呼叫, 讓 No Document Available 的錯誤發生時不至於中斷顯示流程
				this.reDesplay(this._lastAction);
			}
		}
	}

	reDesplay(lastAction){
		switch(lastAction.action){
			case "display": //this.display(lastAction.target);
							this._debounceDisplay(lastAction.target);
						break;
			case "next": 
						 this.next();
						 break;
			case "prev": this.prev();
						 break;
		}
	}

	/**
	 * Remove and Clean Up the Rendition
	 */
	 destroy(){
		
		//移除 injectIdentifier 的呼叫
		this.book.spine.hooks.content.deregister(this.injectIdentifier.bind(this));

		super.destroy();

		// Clear the queue
		this.q.clear();
		this.q = undefined;

		this.views = null;

		this.hooks.display.clear();
		this.hooks.serialize.clear();
		this.hooks.content.clear();
		this.hooks.layout.clear();
		this.hooks.render.clear();
		this.hooks.show.clear();
		this.hooks = undefined;

		this.themes.destroy();
		this.themes = undefined;

		this.epubcfi = undefined;

		this.starting = undefined;
		this.started = undefined;


		this.initSection = undefined;
		this.epubcfi = undefined;
		this.annotations = undefined;
		this._lastAction = undefined;
		this._swipData = undefined; 
		this._clickData = undefined;
		this._lastEventData = undefined; 
		this._lastEventContents = undefined;

		this.location = undefined;
		this._element = undefined;

		clearTimeout(this._dblClickCounter);
		
	}

	//實驗事件
	testAppendItem(){
		this.emit(EVENTS.RENDITION.APPEND_ITEM);
	}
	testDeleteItem(){
		this.emit(EVENTS.RENDITION.DELETE_ITEM);
	}
	testResizeItem(){
		this.emit(EVENTS.RENDITION.RESIZE_ITEM);
	}
}

export default ExRendition;